/**
 * <palette-photo-builder>
 * ------------------------------------------------------------------
 * Self-contained vanilla custom element for the "Palette from a Photo"
 * section. Does NOT use Alpine (the theme's Alpine components only exist
 * inside the compiled global bundle, which we can't extend) and does NOT
 * touch the theme's importmap — assets/palette-math.js is loaded with a
 * dynamic import() the first time the customer interacts (drops/selects/
 * pastes a photo), keeping it out of the initial page weight.
 *
 * Cart integration uses the theme's real event bus (see
 * assets/theme-events.js): after a successful /cart/add.js call we
 * dispatch `theme:product:add` (which the theme's own listener turns into
 * `theme:cart:change`) plus `theme:update:cart` (triggers the theme's
 * section-based cart refresh) and `theme:open:cart-drawer` (opens the
 * drawer). We do not invent a new refresh mechanism.
 *
 * Known gaps / where future work hooks in (see also the build report):
 *   - Web Worker offload for k-means: `runClustering()` below is a single
 *     pure function call (image data in, clusters out) specifically so it
 *     can be handed to a worker via postMessage later without restructuring
 *     the caller. Not implemented in this pass.
 *   - Download/share PNG, lock & reshuffle, eyedropper, saved palettes:
 *     explicitly out of scope per the brief. A `renderResults()` hook and
 *     the per-swatch `<li>` markup are the natural attachment points.
 */

const HEIC_EXTENSIONS = ['.heic', '.heif'];
const MAX_FILE_BYTES = 20 * 1024 * 1024; // ~20MB
// Raised from 200 to 500 (colour-quality brief item 3): at 200px a detail
// that's 40px across in a 4000px photo becomes ~2px and gets blended away
// by bilinear downscaling before it ever reaches the clusterer. ~250k
// pixels at 500px is still fast enough for k-means; see _scheduleRegenerate
// for the mitigation used if live regen during a brush stroke gets janky at
// higher k, instead of dropping this back down.
const DOWNSCALE_TARGET_PX = 500;

// CIEDE2000 parametric weights used when matching a cluster's representative
// colour against catalogue products (colour-quality brief item 4). A potter
// can thin an underglaze with water to lighten it, or layer it up to deepen
// it — lightness is adjustable in this medium. Hue is not: you cannot turn a
// red into an orange by application technique. So a match that's the right
// hue but too dark is usable; a match that's the right lightness but the
// wrong hue is wrong. Raising K_LIGHTNESS discounts lightness error in the
// match cost accordingly. This is a domain decision about the medium, not a
// fudge factor — keep the reasoning here if it's ever revisited.
const K_LIGHTNESS = 1.75;
const K_CHROMA = 1.0;
const K_HUE = 1.0;

// Near-duplicate swatch dedup (brief item 6): two assigned product colours
// this close together read as "the same colour twice" rather than a
// deliberate five-colour palette.
const NEAR_DUPLICATE_DELTA_E = 8;
const MAX_DEDUP_PASSES = 5;

// Brush size scale (kiwi-brush-trail-and-sizing-brief.md, Part 2). Size is
// stored as a FRACTION OF THE IMAGE'S LONGEST EDGE, never in display px, so
// behaviour is identical regardless of upload dimensions or viewport width
// (fixes a real bug: the old code used `_workingWidth` specifically, so a
// portrait photo — where height is the longer edge — got a differently
// sized brush than a landscape photo at the same slider position).
const BRUSH_MIN_FRACTION = 0.012; // 1.2% of longest edge — fine detail
const BRUSH_MAX_FRACTION = 0.4; // 40% — a couple of swipes covers a subject
const BRUSH_DEFAULT_FRACTION = 0.12; // 12% — highlighter, not pencil

// Both of the constants below are DIAMETERS (not radii), matching the
// brief's own clamp formula `Math.max((fraction * longestEdge) / 2,
// BRUSH_MIN_WORKING_PX / 2, <display floor> / 2)` — every term in that
// Math.max is halved on its way to becoming a radius, `fraction *
// longestEdge` included, so all three inputs are being treated the same
// way (diameter in, /2 for radius).
const BRUSH_MIN_WORKING_PX = 4; // diameter floor, working-image px
const BRUSH_MIN_DISPLAY_PX = 10; // diameter floor, CSS px (screen-space touch target)

// How long the true-size brush preview (Part 2, "Feedback while adjusting")
// stays visible after the customer stops moving the slider/nudging keys.
const BRUSH_PREVIEW_FADE_MS = 800;

class PalettePhotoBuilder extends HTMLElement {
  constructor() {
    super();
    this._paletteMath = null;
    this._paletteMathPromise = null;
    this._catalogue = []; // [{id, title, handle, hex, url, image, variants, lab}]
    this._pixelsLab = null; // cached Lab pixels actually fed to k-means (post mask/filter)
    this._pixelsLabFull = null; // cached, unfiltered per-pixel Lab of the current photo
    this._workingWidth = 0; // downscaled working canvas dimensions
    this._workingHeight = 0;
    this._imageObjectUrl = null;
    this._currentCount = 5;
    this._currentMatches = []; // [{cluster, product, distance, costRow, rankedCandidates}]
    this._currentCandidates = []; // candidate products used to build the current cost matrix
    this._variantSelections = new Map(); // product.id -> chosen variant object
    // Keyed by MATCH OBJECT REFERENCE, not product id (kiwi-allow-duplicates
    // brief, section 5): once duplicates are allowed, two different slots
    // can hold the same product, and a product-id key would conflate their
    // panels (opening one would open/close both). Each match object in
    // `this._currentMatches` is a stable reference across re-renders within
    // the same generated palette — it only changes identity on a fresh
    // cluster run (`_runClusteringAndRender`, which clears this Set anyway)
    // or a `_dismissSwatch` splice (which explicitly removes its entry).
    this._revealedAlternativesFor = new Set(); // match objects whose near-miss alternatives panel is open
    this._addingToCart = false;

    // Paint-to-select state (see "Paint to select" section below).
    this._mask = null; // Uint8Array(workingWidth*workingHeight), 1 = include; null = whole photo
    this._undoStack = []; // stack of previous Uint8Array masks, capped at 10
    this._paintMode = false;
    this._activePointerId = null;
    this._lastPaintX = 0;
    this._lastPaintY = 0;
    this._brushRadius = 0; // working-image px, derived from the brush slider
    this._regenerateTimer = null;

    // Stroke trail (kiwi-brush-trail-and-sizing-brief.md, Part 1): a
    // snapshot of `this._mask` as it was at the most recent `pointerdown`,
    // so the trail canvas can diff live-mask-vs-snapshot to tint only what
    // THIS stroke has newly covered, not the whole selection. Null whenever
    // no stroke is active.
    this._strokeStartMask = null;
    this._trailMaskCanvas = null; // offscreen working-resolution scratch buffer, reused across frames
    this._cachedAccentRgb = null;

    // Brush true-size preview (Part 2, "Feedback while adjusting").
    this._brushPreviewActive = false;
    this._brushPreviewTimer = null;

    // Marching-ants outline state (replaces the old translucent-tint
    // overlay — see the "Paint to select: outline rendering" section
    // below). Contours are vector paths in WORKING-image space, cached and
    // only retraced when the mask actually changes (brief 3.1/3.5).
    this._contours = []; // Array<Array<{x,y}>> in working-image coordinates
    this._antsDashOffset = 0; // unscaled (CSS-px) dash offset, advanced ~1/frame at the throttled rate
    this._antsRafId = null;
    this._antsLastTick = 0;
    this._reducedMotionQuery = null; // cached MediaQueryList; `.matches` re-read live each check
    this._onAntsFrame = this._onAntsFrame.bind(this);

    // Brush-cursor (hover/drag circle) state, in DISPLAY (CSS px, relative
    // to the overlay canvas box) coordinates — no working-space transform
    // needed for position, only for the radius (brief 3.4).
    this._hoverDisplayX = null;
    this._hoverDisplayY = null;
    this._hoverVisible = false;
    this._pointerType = null;

    // Mobile full-screen paint mode (kiwi-mobile-and-tracking brief, Part 1
    // Fix 2). See `_enterFullscreenPaintMode`/`_exitFullscreenPaintMode`.
    this._fullscreenActive = false;
    this._fullscreenMediaQuery = null; // cached MediaQueryList, `.matches` read live
    this._savedScrollY = null; // captured on entry, consumed on exit
    this._fullscreenResizeHandler = null;
    this._popstateHandler = null;
  }

  connectedCallback() {
    this._readConfig();
    this._loadCatalogue();
    this._cacheDom();
    this._bindEvents();
    this._initBrushSlider();
    this._maybeRenderDebugView();
  }

  _readConfig() {
    const ds = this.dataset;
    this._config = {
      paletteMathSrc: ds.paletteMathSrc,
      defaultCount: parseInt(ds.defaultCount, 10) || 5,
      minCount: parseInt(ds.minCount, 10) || 3,
      maxCount: parseInt(ds.maxCount, 10) || 8,
      preferredVariantTitle: (ds.preferredVariantTitle || '').trim(),
      ignoreExtremes: ds.ignoreExtremes === 'true',
      showSourceSwatch: ds.showSourceSwatch === 'true',
      enableDownload: ds.enableDownload === 'true',
      ctaText: ds.ctaText || 'Add my palette to cart',
      moneyFormat: ds.moneyFormat || '${{amount}}',
    };
    this._currentCount = this._config.defaultCount;
  }

  _loadCatalogue() {
    const dataScript = this.querySelector('[data-kiwi-palette-data]');
    const skippedScript = this.querySelector('[data-kiwi-palette-skipped]');

    try {
      this._catalogue = dataScript ? JSON.parse(dataScript.textContent) : [];
    } catch (e) {
      console.error('[palette-photo-builder] Could not parse catalogue JSON', e);
      this._catalogue = [];
    }

    try {
      const skipped = skippedScript ? JSON.parse(skippedScript.textContent) : [];
      if (skipped.length) {
        console.warn(
          `[palette-photo-builder] ${skipped.length} product(s) skipped from palette matching ` +
            `because they have no custom.swatch_hex metafield value:`,
          skipped.map((p) => p.title)
        );
      }
    } catch (e) {
      console.error('[palette-photo-builder] Could not parse skipped-products JSON', e);
    }
  }

  _cacheDom() {
    this.$dropzone = this.querySelector('[data-palette-dropzone]');
    this.$fileInput = this.querySelector('[data-palette-file-input]');
    this.$error = this.querySelector('[data-palette-error]');
    this.$controls = this.querySelector('[data-palette-controls]');
    this.$countBtns = Array.from(this.querySelectorAll('[data-palette-count-btn]'));
    this.$startOver = this.querySelector('[data-palette-start-over]');
    this.$status = this.querySelector('[data-palette-status]');
    this.$results = this.querySelector('[data-palette-results]');
    this.$photoPreview = this.querySelector('[data-palette-photo-preview]');
    this.$swatchList = this.querySelector('[data-palette-swatch-list]');
    this.$addToCart = this.querySelector('[data-palette-add-to-cart]');
    this.$ctaTotal = this.querySelector('[data-palette-cta-total]');
    this.$cartError = this.querySelector('[data-palette-cart-error]');

    // Paint to select.
    this.$paintAffordance = this.querySelector('[data-palette-paint-affordance]');
    this.$paintToggle = this.querySelector('[data-palette-paint-toggle]');
    this.$paintControls = this.querySelector('[data-palette-paint-controls]');
    this.$paintOverlay = this.querySelector('[data-palette-paint-overlay]');
    this.$strokeTrail = this.querySelector('[data-palette-stroke-trail]');
    this.$brushSlider = this.querySelector('[data-palette-brush-slider]');
    this.$undo = this.querySelector('[data-palette-undo]');
    this.$clearAll = this.querySelector('[data-palette-clear-all]');
    this.$useWholePhoto = this.querySelector('[data-palette-use-whole-photo]');
    this.$guard = this.querySelector('[data-palette-guard]');
    this.$fullscreenDone = this.querySelector('[data-palette-fullscreen-done]');
    this.$resultsRow = this.querySelector('.palette-builder__results-row');
    this.$photoFrame = this.querySelector('[data-palette-photo-frame]');
    this.$fullscreenCount = this.querySelector('[data-palette-fullscreen-count]');
    this.$fullscreenCountValue = this.querySelector('[data-palette-fullscreen-count-value]');
    this.$fullscreenCountMinus = this.querySelector('[data-palette-fullscreen-count-minus]');
    this.$fullscreenCountPlus = this.querySelector('[data-palette-fullscreen-count-plus]');
  }

  // ---------------------------------------------------------------------
  // Per-colour size selection: every swatch gets its own size dropdown
  // (defaulting to `preferred_variant_title`, e.g. 100ml), independent of
  // every other swatch — see `_variantSelections`, keyed by product id,
  // populated/read in `_renderResults()` and consumed at add-to-cart time.
  // ---------------------------------------------------------------------
  _defaultVariantFor(product) {
    return pickVariant(product, this._config.preferredVariantTitle);
  }

  _selectedVariantFor(product) {
    if (!this._variantSelections.has(product.id)) {
      this._variantSelections.set(product.id, this._defaultVariantFor(product));
    }
    return this._variantSelections.get(product.id);
  }

  _bindEvents() {
    // Click-to-browse + keyboard activation on the dropzone.
    this.$dropzone.addEventListener('click', () => this.$fileInput.click());
    this.$dropzone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.$fileInput.click();
      }
    });

    this.$fileInput.addEventListener('change', (e) => {
      const file = e.target.files && e.target.files[0];
      if (file) this._handleFile(file);
    });

    // Drag and drop.
    ['dragenter', 'dragover'].forEach((evt) => {
      this.$dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        this.$dropzone.classList.add('is-dragover');
      });
    });
    ['dragleave', 'drop'].forEach((evt) => {
      this.$dropzone.addEventListener(evt, (e) => {
        e.preventDefault();
        this.$dropzone.classList.remove('is-dragover');
      });
    });
    this.$dropzone.addEventListener('drop', (e) => {
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this._handleFile(file);
    });

    // Paste from clipboard, anywhere on the page while this element exists.
    this._pasteHandler = (e) => {
      const items = e.clipboardData && e.clipboardData.items;
      if (!items) return;
      for (const item of items) {
        if (item.type && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            this._handleFile(file);
            break;
          }
        }
      }
    };
    document.addEventListener('paste', this._pasteHandler);

    // Colour-count segmented control.
    this.$countBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const count = parseInt(btn.dataset.count, 10);
        this._setCount(count);
      });
    });

    // Full-screen colour-count stepper (kiwi-mobile-overhaul-fix, Problem
    // 1): calls the exact same `_setCount()` as the button row above, so
    // there is one source of truth regardless of which control changed it.
    if (this.$fullscreenCountMinus) {
      this.$fullscreenCountMinus.addEventListener('click', () => {
        this._setCount(Math.max(this._config.minCount, this._currentCount - 1));
      });
    }
    if (this.$fullscreenCountPlus) {
      this.$fullscreenCountPlus.addEventListener('click', () => {
        this._setCount(Math.min(this._config.maxCount, this._currentCount + 1));
      });
    }

    this.$startOver.addEventListener('click', () => this._reset());
    this.$addToCart.addEventListener('click', () => this._addPaletteToCart());

    // Paint to select — see the "Paint to select" section below for the
    // handlers themselves.
    this.$paintToggle.addEventListener('click', () => this._enterPaintMode());
    this.$undo.addEventListener('click', () => this._handleUndo());
    this.$clearAll.addEventListener('click', () => this._clearAllStrokes());
    this.$useWholePhoto.addEventListener('click', () => this._useWholePhoto());
    this.$brushSlider.addEventListener('input', () => this._onBrushSliderInput());

    // Mobile full-screen paint mode (Part 1 Fix 2). The Done button and the
    // Android hardware back button both exit through the SAME code path:
    // Done just calls `history.back()` (undoing the state pushed on entry),
    // which fires `popstate` exactly like a real back-button press would —
    // see `_popstateHandler` below and `_exitFullscreenPaintMode`, which is
    // never called directly from anywhere else. This is what keeps history
    // state and UI state in sync and avoids a stale history entry that
    // would otherwise need a second back-press to actually leave the page.
    if (this.$fullscreenDone) {
      this.$fullscreenDone.addEventListener('click', () => {
        if (this._fullscreenActive) history.back();
      });
    }
    this._popstateHandler = () => {
      if (this._fullscreenActive) this._exitFullscreenPaintMode();
    };
    window.addEventListener('popstate', this._popstateHandler);

    this.$paintOverlay.addEventListener('pointerdown', (e) => this._onPaintPointerDown(e));
    this.$paintOverlay.addEventListener('pointermove', (e) => this._onPaintPointerMove(e));
    this.$paintOverlay.addEventListener('pointerup', (e) => this._onPaintPointerEnd(e));
    this.$paintOverlay.addEventListener('pointercancel', (e) => this._onPaintPointerEnd(e));
    this.$paintOverlay.addEventListener('pointerleave', (e) => this._onPaintPointerLeave(e));

    // Marching-ants lifecycle (brief 3.3): pause the rAF loop while the tab
    // is hidden, resume it if paint mode is still active and there's still
    // a selection when it becomes visible again.
    this._visibilityHandler = () => this._ensureAntsLoop();
    document.addEventListener('visibilitychange', this._visibilityHandler);

    // Keep the outline crisp/aligned across viewport-width changes — the
    // canvas is sized to DISPLAY resolution × DPR (see `_drawAntsFrame`),
    // so a resize needs a redraw to match the new box size.
    this._resizeHandler = () => {
      if (this._paintMode) {
        this._drawAntsFrame();
        this._drawTrailFrame();
      }
    };
    window.addEventListener('resize', this._resizeHandler);

    // Keyboard brush-size nudging (brief Part 2, "Also worth adding"):
    // scoped to paint mode + not while the customer is typing elsewhere on
    // the page (see `_onKeydown` for the exact guard).
    this._keydownHandler = (e) => this._onKeydown(e);
    document.addEventListener('keydown', this._keydownHandler);
  }

  _maybeRenderDebugView() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('palette_debug') !== '1') return;

    const debugRoot = this.querySelector('[data-palette-debug]');
    const tbody = this.querySelector('[data-palette-debug-tbody]');
    if (!debugRoot || !tbody) return;

    debugRoot.classList.remove('hidden');
    tbody.innerHTML = '';

    this._catalogue.forEach((product) => {
      const available = product.variants.some((v) => v.available);
      const tr = document.createElement('tr');

      const swatchTd = document.createElement('td');
      swatchTd.className = 'p-1';
      const swatch = document.createElement('span');
      swatch.style.display = 'inline-block';
      swatch.style.width = '1.5rem';
      swatch.style.height = '1.5rem';
      swatch.style.borderRadius = '9999px';
      swatch.style.background = product.hex;
      swatchTd.appendChild(swatch);

      const nameTd = document.createElement('td');
      nameTd.className = 'p-1';
      nameTd.textContent = product.title;

      const hexTd = document.createElement('td');
      hexTd.className = 'p-1';
      hexTd.textContent = product.hex;

      const availTd = document.createElement('td');
      availTd.className = 'p-1';
      availTd.textContent = available ? 'In stock' : 'Sold out';

      tr.append(swatchTd, nameTd, hexTd, availTd);
      tbody.appendChild(tr);
    });
  }

  // ---------------------------------------------------------------------
  // Lazy-load the colour maths module. Only called once, on first upload.
  // ---------------------------------------------------------------------
  _ensurePaletteMath() {
    if (!this._paletteMathPromise) {
      this._paletteMathPromise = import(this._config.paletteMathSrc).then((mod) => {
        this._paletteMath = mod;
        return mod;
      });
    }
    return this._paletteMathPromise;
  }

  // ---------------------------------------------------------------------
  // File intake, validation, decode, downscale
  // ---------------------------------------------------------------------
  async _handleFile(file) {
    this._clearError();
    this._track('palette_image_uploaded', {
      file_size: file.size,
      file_type: file.type,
    });

    // Non-image file.
    const looksLikeHeic = HEIC_EXTENSIONS.some((ext) =>
      file.name && file.name.toLowerCase().endsWith(ext)
    );

    if (!file.type.startsWith('image/') && !looksLikeHeic) {
      this._showError(
        "That doesn't look like an image file. Please choose a JPEG, PNG, WebP, or similar image."
      );
      return;
    }

    // Large file.
    if (file.size > MAX_FILE_BYTES) {
      this._showError(
        "That image is too large (over 20MB). Try a smaller image, or a screenshot of it."
      );
      return;
    }

    let bitmap;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (err) {
      if (looksLikeHeic || file.type === 'image/heic' || file.type === 'image/heif') {
        this._showError(
          "We can't read HEIC images yet — try a screenshot or a JPEG."
        );
      } else {
        this._showError(
          "We couldn't open that image — it may be corrupted. Try a different file."
        );
      }
      console.error('[palette-photo-builder] decode failed', err);
      return;
    }

    try {
      await this._loadMathAndBuildPalette(bitmap, file);
    } catch (err) {
      console.error('[palette-photo-builder] processing failed', err);
      this._showError(
        "Something went wrong while building your palette. Please try again."
      );
    } finally {
      if (bitmap.close) bitmap.close();
    }
  }

  async _loadMathAndBuildPalette(bitmap, file) {
    await this._ensurePaletteMath();

    // New photo: clear any paint-mode mask/undo state left over from a
    // previous photo (brief section 7: "new upload clears mask, undo
    // stack, and exits paint mode").
    this._resetMaskState();

    // Downscale to ~DOWNSCALE_TARGET_PX longest edge on an offscreen canvas.
    const longestEdge = Math.max(bitmap.width, bitmap.height);
    const scale = DOWNSCALE_TARGET_PX / longestEdge;
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(bitmap, 0, 0, width, height);

    let imageData;
    try {
      imageData = ctx.getImageData(0, 0, width, height);
    } catch (err) {
      this._showError(
        "We couldn't read the pixels of that image (it may be protected or corrupted)."
      );
      console.error('[palette-photo-builder] getImageData failed', err);
      return;
    }

    const { srgbToLab, hashPixelsForSeed } = this._paletteMath;

    // Convert every pixel to Lab once and cache the full, unfiltered array
    // (plus the working canvas dimensions) so masked re-clustering never
    // has to re-decode the photo or re-run sRGB->Lab — collectPixels()
    // below, and every subsequent re-mask, indexes straight into this.
    const pixelsLabFull = [];
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const lab = srgbToLab(data[i], data[i + 1], data[i + 2]);
      pixelsLabFull.push(lab);
    }

    this._pixelsLabFull = pixelsLabFull;
    this._workingWidth = width;
    this._workingHeight = height;
    this._pixelsSeed = hashPixelsForSeed(data);

    // Unmasked path — must stay byte-identical to the old direct
    // `filterExtremes(pixelsLab, {minL:5, maxL:95})` call it replaces.
    this._pixelsLab = collectPixels(this._pixelsLabFull, {
      mask: null,
      ignoreExtremes: this._config.ignoreExtremes,
    });

    // Brush radius depends on workingWidth, now known for this photo.
    this._onBrushSliderInput();

    // Build a small preview image (object URL) for the "shareable moment" thumbnail.
    if (this._imageObjectUrl) URL.revokeObjectURL(this._imageObjectUrl);
    this._imageObjectUrl = URL.createObjectURL(file);
    this.$photoPreview.src = this._imageObjectUrl;

    this._ensureCatalogueLab();

    if (this._pixelsLab.length === 0) {
      this._showError(
        "Your image didn't have enough non-white/non-black pixels to build a palette. Try turning off " +
          '"ignore near-white and near-black pixels" or use a different image.'
      );
      return;
    }

    if (this._catalogueWithHex().length === 0) {
      this._showError(
        "We don't have colour data for any products in this collection yet, so we can't build a palette right now."
      );
      return;
    }

    this.$controls.classList.remove('hidden');
    this._runClusteringAndRender();
  }

  // ---------------------------------------------------------------------
  // Catalogue prep: convert each product's hex to Lab once, cache it.
  // ---------------------------------------------------------------------
  _ensureCatalogueLab() {
    if (this._catalogueLabReady) return;
    const { srgbToLab } = this._paletteMath;

    this._catalogue.forEach((product) => {
      const rgb = hexToRgb(product.hex);
      if (!rgb) {
        product.lab = null;
        return;
      }
      product.lab = srgbToLab(rgb.r, rgb.g, rgb.b);
      product.available = product.variants.some((v) => v.available);
    });

    this._catalogueLabReady = true;
  }

  _catalogueWithHex() {
    return this._catalogue.filter((p) => p.lab);
  }

  // ---------------------------------------------------------------------
  // Clustering + matching. Structured as a single pure call
  // (`runClustering`) so it could be moved to a Web Worker later via
  // postMessage without reshaping this method — NOT implemented as an
  // actual worker in this pass (see file header + build report).
  // ---------------------------------------------------------------------
  _runClusteringAndRender({ announce = true } = {}) {
    const { kMeansLab, deltaE2000, assignHungarian } = this._paletteMath;

    // Guard rail (brief section 5). Deliberately gated on this._mask being
    // active: the whole-image path must never be able to hit this branch,
    // so it stays byte-identical to before this feature existed.
    if (this._mask && this._pixelsLab.length < 50 * this._currentCount) {
      this._showGuard(`Paint a bit more to build a palette of ${this._currentCount}`);
      return;
    }
    this._clearGuard();

    // Fold the mask into the RNG seed so a changed selection reseeds
    // correctly (brief section 7). Unmasked seed is untouched.
    const seed = this._mask ? (this._pixelsSeed ^ hashMask(this._mask)) >>> 0 : this._pixelsSeed;

    const clusters = runClustering(kMeansLab, this._pixelsLab, this._currentCount, seed);

    // Only match against colours we have hex data for AND that have at
    // least one available variant (brief step 7: exclude unavailable
    // colours from the candidate set).
    const candidates = this._catalogueWithHex().filter((p) => p.available);

    if (candidates.length === 0) {
      this._showError(
        "Every colour we could match against is currently sold out. Please check back soon."
      );
      return;
    }

    // Cost matrix: clusters (rows) x candidate colours (columns). Matched
    // against each cluster's REPRESENTATIVE pixel (colour-quality brief item
    // 1), not the plain centroid — the centroid can land on a washed-out
    // colour that appears nowhere in the photo. Weighted toward hue over
    // lightness (brief item 4 — see K_LIGHTNESS comment above).
    const costMatrix = clusters.map((cluster) =>
      candidates.map((product) =>
        deltaE2000(cluster.representative, product.lab, { kL: K_LIGHTNESS, kC: K_CHROMA, kH: K_HUE })
      )
    );

    let assignment = assignHungarian(costMatrix);

    // Near-duplicate dedup (brief item 6): if two assigned product colours
    // are practically indistinguishable, a palette of N is really a palette
    // of N-1 with a colour repeated in disguise. Reassign the lower-priority
    // (smaller-population) cluster to its own next-best still-unused
    // candidate. This compares actual product colours to each other (plain
    // CIEDE2000, no k-weighting — it's about product-colour similarity, not
    // match quality), independent of the cluster-to-product cost matrix.
    assignment = dedupeNearDuplicateAssignment({
      clusters,
      candidates,
      costMatrix,
      assignment,
      deltaE2000,
    });

    // Any previously-revealed alternatives panels refer to a palette that no
    // longer exists once we regenerate — start clean.
    this._revealedAlternativesFor.clear();
    this._currentCandidates = candidates;

    this._currentMatches = clusters.map((cluster, i) => ({
      cluster,
      product: candidates[assignment[i]],
      distance: costMatrix[i][assignment[i]],
      // costRow travels with the match (not indexed separately by slot
      // position) so it stays correct even after a dismiss splices the
      // matches array and shifts indices around.
      costRow: costMatrix[i],
      // Near-miss alternatives (brief item 5): the ranked candidate list
      // already exists as this row of the cost matrix — surface it rather
      // than recomputing. Excludes the product already assigned to *this*
      // slot; may include candidates assigned elsewhere in the palette.
      rankedCandidates: rankCandidatesForRow(costMatrix[i], candidates, assignment[i]).slice(0, 3),
    }));

    this._renderResults();

    this._track('palette_generated', { colour_count: this._currentCount });
    if (announce) this._announce(`Palette generated with ${this._currentCount} colours.`);
  }

  _setCount(count) {
    if (count === this._currentCount || !this._pixelsLab) {
      this._currentCount = count;
      this._updateCountButtons();
      return;
    }
    this._currentCount = count;
    this._updateCountButtons();
    this._runClusteringAndRender();
  }

  _updateCountButtons() {
    this.$countBtns.forEach((btn) => {
      const isActive = parseInt(btn.dataset.count, 10) === this._currentCount;
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    // Keep the full-screen stepper in sync regardless of which control
    // actually changed the count (kiwi-mobile-overhaul-fix, Problem 1).
    if (this.$fullscreenCountValue) {
      this.$fullscreenCountValue.textContent = String(this._currentCount);
    }
    if (this.$fullscreenCountMinus) {
      this.$fullscreenCountMinus.disabled = this._currentCount <= this._config.minCount;
    }
    if (this.$fullscreenCountPlus) {
      this.$fullscreenCountPlus.disabled = this._currentCount >= this._config.maxCount;
    }
  }

  // ---------------------------------------------------------------------
  // Paint to select. Default state has zero selection UI — the mask stays
  // null (whole photo) until the customer taps the "want colours from just
  // part of the photo?" link. See brief sections 3, 4 and 6.
  // ---------------------------------------------------------------------

  // Shows/hides the affordance link vs. the three paint-mode controls
  // (+ overlay canvas) as one unit, so entering/exiting paint mode and the
  // new-photo/start-over reset all stay in sync through one code path.
  _setPaintUiActive(active) {
    if (!this.$paintControls) return;
    this.$paintAffordance.classList.toggle('hidden', active);
    this.$paintControls.classList.toggle('hidden', !active);
    this.$paintOverlay.classList.toggle('hidden', !active);
    // Part 1 Fix 1: `touch-action: none` is only ever applied via this
    // modifier class, toggled in the same call that shows/hides the
    // overlay itself, so it can never outlive paint mode being active.
    this.$paintOverlay.classList.toggle('palette-builder__paint-overlay--active', active);
    if (this.$strokeTrail) this.$strokeTrail.classList.toggle('hidden', !active);
  }

  // kiwi-mobile-overhaul-fix: once a mask exists, re-tapping the affordance
  // resumes editing that selection rather than starting fresh — say so, so
  // it doesn't read as "start over" when it isn't. Reverts to the original
  // wording whenever the mask is actually cleared (`_resetMaskState`,
  // `_useWholePhoto`).
  _updatePaintAffordanceLabel() {
    if (!this.$paintToggle) return;
    this.$paintToggle.textContent = this._mask
      ? 'Edit your selection'
      : 'Click here if you want colours from just part of the image';
  }

  _resetMaskState() {
    this._mask = null;
    this._undoStack = [];
    this._paintMode = false;
    this._activePointerId = null;
    clearTimeout(this._regenerateTimer);
    this._setPaintUiActive(false);
    this._updatePaintAffordanceLabel();
    this._clearGuard();

    this._contours = [];
    this._stopAntsLoop();
    this._hoverVisible = false;
    this._hoverDisplayX = null;
    this._hoverDisplayY = null;

    // Part 1: none of the call sites that route through here (new upload,
    // start-over) are mid-stroke states, so the trail should already be
    // empty — clear defensively anyway rather than assume, per the brief.
    this._strokeStartMask = null;
    clearTimeout(this._brushPreviewTimer);
    this._brushPreviewActive = false;
    this._clearTrailCanvas();

    // Safety net: a new upload or "start over" can happen while mobile
    // full-screen paint mode is active (e.g. the customer backs out via
    // some other control). Never leave the page scroll-locked behind.
    this._maybeExitFullscreen();
  }

  _recomputeAndRender(opts) {
    this._pixelsLab = collectPixels(this._pixelsLabFull, {
      mask: this._mask,
      ignoreExtremes: this._config.ignoreExtremes,
    });
    this._runClusteringAndRender(opts);
  }

  _enterPaintMode() {
    if (this._paintMode || !this._pixelsLabFull) return;
    this._paintMode = true;
    if (!this._mask) {
      this._mask = new Uint8Array(this._workingWidth * this._workingHeight);
    }
    this._undoStack = [];

    this._setPaintUiActive(true);
    this._retraceAndRedraw();
    this._recomputeAndRender();

    this._announce('Paint mode. Paint over the part of the image you want colours from.');
    this.$brushSlider.focus();

    // Part 1 Fix 2: on a coarse-pointer, narrow-viewport device only — a
    // mouse+hover desktop (or a wide/landscape coarse-pointer tablet) never
    // matches this query, so this call, and everything it does, simply
    // never happens there. Desktop paint mode is unaffected by any of this.
    if (this._isMobileFullscreenEligible()) {
      this._enterFullscreenPaintMode();
    }
  }

  _useWholePhoto() {
    this._paintMode = false;
    this._mask = null;
    this._undoStack = [];

    this._setPaintUiActive(false);
    this._updatePaintAffordanceLabel();
    this._retraceAndRedraw();
    this._clearTrailCanvas(); // not a mid-stroke state, but defensive per the brief

    this._recomputeAndRender();
    this._announce('Showing colours from the whole image.');
    this.$paintToggle.focus();

    // "Use whole image" ends paint mode outright — nothing left to paint,
    // so there's no reason to stay in the full-screen paint presentation.
    this._maybeExitFullscreen();
  }

  // ---------------------------------------------------------------------
  // Mobile full-screen paint mode (kiwi-mobile-and-tracking brief, Part 1
  // Fix 2). Switched on purely by a `pointer: coarse` + narrow-viewport
  // media query — never user-agent sniffing — so it is completely absent
  // on desktop regardless of CSS: `_enterPaintMode`/`_exitPaintMode`'s own
  // logic above is untouched, and none of the methods below are ever
  // called unless `_isMobileFullscreenEligible()` returns true first.
  //
  // Entering moves `.palette-builder__results-row`, `.palette-builder__
  // paint-controls`, and `.palette-builder__fullscreen-done` into a portal
  // div appended directly to <body> (see `_enterFullscreenPaintMode` for
  // why: a theme layout wrapper this section sits inside caps any z-index
  // set on elements nested inside it, so painting above the page's own
  // sticky header is only reliably possible by escaping that wrapper via a
  // real DOM move, not by raising a z-index number further). `appendChild`
  // on an already-mounted node MOVES it rather than recreating it, so
  // every element already cached in `_cacheDom` (`this.$paintOverlay`,
  // `this.$brushSlider`, etc.) stays the exact same DOM node with the
  // exact same listeners, and both canvases keep their already-drawn pixel
  // content — only their position in the tree (and so, visually, on
  // screen) changes. `this.classList.add('palette-builder--fullscreen')`
  // still runs too, purely as JS-observable state on the host element —
  // no CSS in palette-from-photo.liquid keys off it any more.
  // ---------------------------------------------------------------------

  // 639px, not a newly invented number: it's one pixel below this section's
  // own existing `min-width: 640px` breakpoint (see the {% style %} block),
  // i.e. exactly the point where `.palette-builder__results-row` is
  // already laid out as the single-column "mobile" version of this UI.
  _isMobileFullscreenEligible() {
    if (!this._fullscreenMediaQuery) {
      this._fullscreenMediaQuery = window.matchMedia('(pointer: coarse) and (max-width: 639px)');
    }
    return this._fullscreenMediaQuery.matches;
  }

  _enterFullscreenPaintMode() {
    if (this._fullscreenActive) return;
    this._fullscreenActive = true;

    // The browser's own automatic scroll restoration on `popstate` (the
    // default, `history.scrollRestoration = 'auto'`) races our own
    // `window.scrollTo()` call in `_exitFullscreenPaintMode` below and
    // wins — confirmed by testing: the explicit restore ran, but the
    // browser then snapped scroll back to 0 anyway once the popstate event
    // finished. Since we're taking manual control of scroll around this
    // one pushed history entry, tell the browser not to also try. Restored
    // to whatever it was before (never assume it was 'auto') on exit.
    this._prevScrollRestoration = history.scrollRestoration;
    history.scrollRestoration = 'manual';

    // The iOS Safari body-lock technique the brief specifies verbatim —
    // `overflow: hidden` on body does not reliably hold there, `position:
    // fixed` does. Stored as instance state so entry/exit stay paired even
    // if something else touches scroll in between.
    this._savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._savedScrollY}px`;
    document.body.style.width = '100%';

    // Portal: move the three fullscreen-relevant elements to be direct
    // children of <body>, rather than trying to out-z-index the page from
    // inside the section. This section sits inside a theme layout wrapper
    // (`position: relative; z-index: 10`) — that wrapper caps every z-index
    // set on anything nested inside it, so no z-index value we set on
    // `.palette-builder__results-row` etc. could ever paint above the
    // sticky header (z-index: 40): confirmed via elementFromPoint()
    // hit-testing, which showed the header winning even against a nested
    // z-index of 9998. `appendChild` on an already-mounted node MOVES it —
    // it does not destroy/recreate it — so every listener already bound
    // (`this.$paintOverlay`, `this.$brushSlider`, etc.) and both canvases'
    // already-drawn pixel content survive the move untouched. This also
    // makes the elements immune to any ancestor's `overflow: hidden`
    // clipping, since <body> itself never clips its own fixed children.
    // Original parent + next-sibling are recorded per node so exit can
    // restore each one to its exact original position, not just "somewhere
    // in the section."
    this._fullscreenPortal = document.createElement('div');
    this._fullscreenPortal.className = 'palette-builder__fullscreen-portal';
    // Also given a real ID (see palette-from-photo.liquid for why: a class-
    // only selector here loses a specificity fight against the base rules'
    // `:is(#section-id, .class)` pattern on every property both happen to
    // set — this was silently breaking the swatch strip's layout).
    this._fullscreenPortal.id = 'palette-builder-fullscreen-portal';
    document.body.appendChild(this._fullscreenPortal);

    this._portalMoves = [this.$resultsRow, this.$paintControls, this.$fullscreenDone, this.$fullscreenCount]
      .filter(Boolean)
      .map((node) => ({ node, parent: node.parentNode, nextSibling: node.nextSibling }));
    this._portalMoves.forEach(({ node }) => this._fullscreenPortal.appendChild(node));

    this.classList.add('palette-builder--fullscreen');
    this._updateFullscreenReservedSpace();
    this._updateCountButtons(); // stepper just entered the DOM — give it today's count/disabled state

    // Marketing/discount popups (Klaviyo, Rebuy, etc.) mount as high-
    // z-index `position: fixed` children of <body> — 90000-100000 in
    // practice, observed on this exact page, well above our own 10000
    // ceiling, so they'd render on top of and be interactable through the
    // full-screen takeover. Reported directly: "we can't have the pop up
    // coming up... without messing up the purpose it is serving
    // elsewhere" — so this only ever HIDES (never removes/destroys)
    // whatever's currently on screen for the duration of full-screen paint
    // mode, and watches for new ones triggering while it's open; the app
    // itself, its own triggers, and its behavior on every other page are
    // untouched. See `_suppressExternalPopups`/`_restoreExternalPopups`.
    this._suppressExternalPopups();

    if (!this._fullscreenResizeHandler) {
      this._fullscreenResizeHandler = () => {
        this._updateFullscreenReservedSpace();
        this._updateFullscreenPhotoFrame();
      };
      window.addEventListener('resize', this._fullscreenResizeHandler);
    }

    // Android hardware back button: push a marker state so a back-button
    // press fires `popstate` (handled by `_popstateHandler`, bound once in
    // `_bindEvents`) instead of navigating away from the page. The Done
    // button (see `_bindEvents`) exits through the identical path by
    // calling `history.back()` itself, so there is only ever one exit
    // implementation (`_exitFullscreenPaintMode`, below).
    history.pushState({ paletteFullscreen: true }, '', location.href);

    // The overlay canvases size themselves from getBoundingClientRect()
    // (see `_resizeCanvasForDisplay`), which has just changed — redraw
    // once the new fixed layout has actually painted. `_updateFullscreen
    // PhotoFrame` runs FIRST so the canvases measure themselves against
    // the frame's final, exact-to-the-photo size (kiwi-mobile-overhaul-
    // fix, Problem 2) rather than the full letterboxed area.
    requestAnimationFrame(() => {
      this._updateFullscreenPhotoFrame();
      this._retraceAndRedraw();
      this._drawTrailFrame();
    });
  }

  // The paint canvas is `inset: 0` on `.palette-builder__photo-frame`, not
  // on the full (often letterboxed, under `object-fit: contain`) available
  // area — otherwise a tap in the empty margin around the photo still
  // lands on the canvas and can register as a paint stroke, exactly the
  // "selections outside the image" bug reported. This sizes the frame, in
  // real pixels, to the image's ACTUAL rendered box: the same math the
  // browser does internally for `object-fit: contain`, done here so the
  // frame (and so the canvas) can match it exactly instead of covering the
  // whole stage. Runs once on entry (after layout has settled — see the
  // rAF above) and again on every resize/orientation change.
  _updateFullscreenPhotoFrame() {
    if (!this._fullscreenActive || !this.$photoFrame || !this.$photoPreview) return;
    const img = this.$photoPreview;
    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;
    if (!naturalW || !naturalH) return;

    const stage = this.$photoFrame.parentElement; // `.palette-builder__photo`, the flex centering area
    const availW = stage.clientWidth;
    const availH = stage.clientHeight;
    if (!availW || !availH) return;

    const scale = Math.min(availW / naturalW, availH / naturalH);
    this.$photoFrame.style.width = `${Math.round(naturalW * scale)}px`;
    this.$photoFrame.style.height = `${Math.round(naturalH * scale)}px`;
  }

  // Reads the *actual rendered height* of the (now `position: fixed`)
  // bottom control bar and exposes it as a CSS custom property so
  // `.palette-builder__results-row` can reserve exactly that much space
  // above it, rather than the CSS guessing a fixed pixel budget that would
  // drift out of sync if the control bar's content ever wraps to more/
  // fewer lines.
  _updateFullscreenReservedSpace() {
    if (!this._fullscreenActive || !this.$paintControls || !this._fullscreenPortal) return;
    const height = this.$paintControls.getBoundingClientRect().height;
    // Set on the portal, not `this` — `.palette-builder__results-row` now
    // lives inside the portal (a <body> child), a different DOM subtree
    // from `<palette-photo-builder>`, so a custom property set on `this`
    // would no longer cascade down to it.
    this._fullscreenPortal.style.setProperty('--palette-fullscreen-bottom-space', `${height}px`);
  }

  // Highest z-index we ever use ourselves (the Done button/count stepper,
  // both 10000) — anything ABOVE this, fixed-position, and a direct child
  // of <body> is assumed to be a third-party overlay (observed on this
  // exact page: a Klaviyo signup popup and a Rebuy discount bubble, at
  // z-index 90000-100000) rather than anything of ours, and gets hidden
  // for the duration of full-screen paint mode. kiwi-mobile-overhaul-fix:
  // reported directly that these can't be allowed to appear over the
  // paint UI, but "without messing up the purpose it is serving
  // elsewhere" — so this only ever sets `display: none` (never removes or
  // destroys the element, never touches the app's own trigger logic) and
  // restores each one's own previous inline `display` value exactly on
  // exit, so the popup app itself is completely unaffected on every other
  // page, and even on this page before/after full-screen mode.
  static get FULLSCREEN_POPUP_Z_THRESHOLD() {
    return 10000;
  }

  _suppressExternalPopups() {
    this._suppressedPopups = [];
    const maybeSuppress = (el) => {
      if (!(el instanceof HTMLElement) || el === this._fullscreenPortal) return;
      const cs = getComputedStyle(el);
      if (cs.position === 'fixed' && parseInt(cs.zIndex || '0', 10) > PalettePhotoBuilder.FULLSCREEN_POPUP_Z_THRESHOLD) {
        this._suppressedPopups.push({ el, prevDisplay: el.style.display });
        el.style.setProperty('display', 'none', 'important');
      }
    };

    // Whatever's already open right now (e.g. a popup that opened before
    // the customer tapped into paint mode).
    Array.from(document.body.children).forEach(maybeSuppress);

    // Whatever triggers itself WHILE full-screen mode is open (these apps
    // commonly run on their own timers/scroll-triggers, independent of
    // anything we do).
    this._popupObserver = new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach(maybeSuppress));
    });
    this._popupObserver.observe(document.body, { childList: true });
  }

  _restoreExternalPopups() {
    if (this._popupObserver) {
      this._popupObserver.disconnect();
      this._popupObserver = null;
    }
    if (this._suppressedPopups) {
      this._suppressedPopups.forEach(({ el, prevDisplay }) => {
        el.style.display = prevDisplay;
      });
      this._suppressedPopups = null;
    }
  }

  // The ONLY place that ever reverses the full-screen presentation — both
  // the Done button and the Android back button reach this exclusively via
  // `popstate` (see `_bindEvents`), never by calling it directly, so the
  // two exit routes can never fall out of sync with each other or with
  // browser history state.
  //
  // kiwi-mobile-overhaul-fix (Problem 3 — the real disaster report):
  // exiting used to leave `_paintMode` (and so the hot, touch-eating
  // overlay) running, on the theory that "Done" only closes the
  // full-screen VIEW. That's backwards on mobile specifically: a coarse-
  // pointer narrow viewport has no safe "paint mode is on but not full-
  // screen" state — that gesture conflict (one-finger-drag means both
  // "paint" and "scroll") is the ENTIRE reason full-screen mode exists in
  // the first place. Leaving it half-on meant a customer who tapped Done
  // came back to a normal-looking page where a scroll gesture anywhere on
  // the photo silently kept painting instead. So exiting here now also
  // fully deactivates LIVE painting — but, unlike `_useWholePhoto()` or a
  // fresh upload/start-over, it does NOT touch `this._mask`/
  // `this._undoStack`: the selection and the palette generated from it are
  // exactly as the brief specifies, "intact." Re-tapping the affordance
  // (now relabeled "Edit your selection" by `_updatePaintAffordanceLabel`
  // whenever a mask exists) calls `_enterPaintMode()` again, which reuses
  // the existing mask rather than starting over, and goes straight back to
  // full-screen. This whole method only ever runs on mobile (nothing else
  // ever sets `_fullscreenActive`), so desktop's own paint-mode toggling
  // is untouched by any of this.
  _exitFullscreenPaintMode() {
    if (!this._fullscreenActive) return;
    this._fullscreenActive = false;

    this.classList.remove('palette-builder--fullscreen');

    // Move every node back to its exact original parent + position (not
    // just "back into the section somewhere") before discarding the portal.
    if (this._portalMoves) {
      this._portalMoves.forEach(({ node, parent, nextSibling }) => {
        parent.insertBefore(node, nextSibling);
      });
      this._portalMoves = null;
    }
    if (this._fullscreenPortal) {
      this._fullscreenPortal.remove();
      this._fullscreenPortal = null;
    }

    // Falls back to the base CSS rule (100%/100%, i.e. "fill .photo") now
    // that we're back in normal/inline layout — see `_updateFullscreen
    // PhotoFrame`.
    if (this.$photoFrame) {
      this.$photoFrame.style.width = '';
      this.$photoFrame.style.height = '';
    }

    this._restoreExternalPopups();

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this._savedScrollY || 0);
    this._savedScrollY = null;

    if (this._prevScrollRestoration) {
      history.scrollRestoration = this._prevScrollRestoration;
      this._prevScrollRestoration = null;
    }

    if (this._fullscreenResizeHandler) {
      window.removeEventListener('resize', this._fullscreenResizeHandler);
      this._fullscreenResizeHandler = null;
    }

    // The actual fix for Problem 3 — see the method-level comment above.
    // `_setPaintUiActive(false)` hides the overlay/trail canvases, so there
    // is nothing left on screen that needs retracing after this — unlike
    // entry, no trailing redraw here.
    this._paintMode = false;
    this._setPaintUiActive(false);
    this._stopAntsLoop();
    this._updatePaintAffordanceLabel();
  }

  // Forces an exit from full-screen presentation from a call site that
  // ISN'T the Done button or the back button (e.g. a fresh upload or
  // "start over" happening while still in full-screen paint mode) — routed
  // through the exact same `history.back()` -> `popstate` ->
  // `_exitFullscreenPaintMode()` path as the two named exits, so it can
  // never leave a stale, unpaired history entry behind.
  _maybeExitFullscreen() {
    if (this._fullscreenActive) history.back();
  }

  _handleUndo() {
    if (this._undoStack.length === 0) return;
    this._mask = this._undoStack.pop();
    this._retraceAndRedraw();
    this._clearTrailCanvas(); // not a mid-stroke state, but defensive per the brief
    clearTimeout(this._regenerateTimer);
    this._recomputeAndRender({ announce: true });
  }

  // Wipes every stroke back to an empty selection, but — unlike "Use whole
  // image" — stays in paint mode so the customer can start painting again
  // right away. The pre-clear mask still goes on the undo stack, so Undo
  // can bring the strokes back.
  _clearAllStrokes() {
    if (!this._mask) return;
    this._undoStack.push(this._mask.slice());
    if (this._undoStack.length > 10) this._undoStack.shift();

    this._mask = new Uint8Array(this._workingWidth * this._workingHeight);
    this._retraceAndRedraw();
    this._clearTrailCanvas(); // not a mid-stroke state, but defensive per the brief
    clearTimeout(this._regenerateTimer);
    this._recomputeAndRender({ announce: true });
  }

  // ---------------------------------------------------------------------
  // Brush size (kiwi-brush-trail-and-sizing-brief.md, Part 2). The slider
  // runs 0-1 internally and maps geometrically (`sliderToFraction`, near the
  // bottom of the file) onto a fraction of the image's longest edge, so
  // behaviour is identical for a portrait photo, a landscape photo, and any
  // viewport width — see `_computeBrushRadius`.
  // ---------------------------------------------------------------------

  // Sets the slider's initial position from BRUSH_DEFAULT_FRACTION (via the
  // inverse of sliderToFraction) rather than hardcoding a slider value, so
  // the constants stay the single source of truth (brief's own wording).
  // Called once from connectedCallback, before any photo has been uploaded.
  _initBrushSlider() {
    if (!this.$brushSlider) return;
    this.$brushSlider.value = String(fractionToSlider(BRUSH_DEFAULT_FRACTION));
    this._onBrushSliderInput({ preview: false });
  }

  // Working-image-px radius for the current slider position, per the
  // brief's three-way clamp: the fraction-of-longest-edge size, a 4px
  // (diameter) working-resolution floor, and a 10px (diameter) floor
  // converted from CSS/display px into working px — whichever is largest.
  _computeBrushRadius(t) {
    const fraction = sliderToFraction(t);
    const longestEdge = Math.max(this._workingWidth, this._workingHeight);
    const fractionRadiusPx = (fraction * longestEdge) / 2;
    const workingFloorRadiusPx = BRUSH_MIN_WORKING_PX / 2;

    // Convert the CSS-px display floor into working-image px by inverting
    // workingToDisplayScale (that helper gives display-px-per-working-px,
    // so we need 1/scale to go the other way) — this makes the clamp mean
    // the same physical on-screen size regardless of how large the photo
    // box happens to render (brief: "identically on desktop and a phone").
    let displayFloorRadiusPx = workingFloorRadiusPx;
    const rect = this.$paintOverlay ? this.$paintOverlay.getBoundingClientRect() : null;
    const displayWidth = rect && rect.width ? rect.width : 0;
    const displayHeight = rect && rect.height ? rect.height : 0;
    if (displayWidth && displayHeight) {
      const scale = workingToDisplayScale({
        workingWidth: this._workingWidth,
        workingHeight: this._workingHeight,
        naturalWidth: this.$photoPreview.naturalWidth || this._workingWidth,
        naturalHeight: this.$photoPreview.naturalHeight || this._workingHeight,
        displayWidth,
        displayHeight,
      });
      if (scale > 0) {
        displayFloorRadiusPx = (BRUSH_MIN_DISPLAY_PX / 2) / scale;
      }
    }

    return Math.max(fractionRadiusPx, workingFloorRadiusPx, displayFloorRadiusPx);
  }

  // A words-only description for aria-valuetext — the slider has no numeric
  // label (brief: "Don't label the slider with a percentage"), but screen
  // reader users still need SOME sense of relative size.
  _brushSizeDescription(t) {
    if (t < 0.25) return 'Small brush';
    if (t < 0.6) return 'Medium brush';
    if (t < 0.85) return 'Large brush';
    return 'Extra large brush';
  }

  _onBrushSliderInput({ preview = true } = {}) {
    if (!this.$brushSlider) return;
    const raw = Number(this.$brushSlider.value);
    const t = Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : fractionToSlider(BRUSH_DEFAULT_FRACTION);

    if (this._workingWidth && this._workingHeight) {
      this._brushRadius = this._computeBrushRadius(t);
    }

    this.$brushSlider.setAttribute('aria-valuetext', this._brushSizeDescription(t));

    if (preview) this._showBrushPreview();
  }

  // True-size preview (brief: "show the brush circle at true size, centred
  // on the image" while the slider is being adjusted, fading ~800ms after
  // the customer stops). Rendered on the ants canvas — see the
  // compositing-architecture note above `_drawAntsFrame` for why.
  _showBrushPreview() {
    clearTimeout(this._brushPreviewTimer);
    if (!this._paintMode) return;

    this._brushPreviewActive = true;
    this._drawAntsFrame();

    this._brushPreviewTimer = setTimeout(() => {
      this._brushPreviewActive = false;
      this._drawAntsFrame();
    }, BRUSH_PREVIEW_FADE_MS);
  }

  // Keyboard brush-size nudging (brief Part 2, "Also worth adding"): `[`
  // and `]` step the slider down/up. Scoped so it never hijacks ordinary
  // page typing: only acts while paint mode is active, and bails whenever
  // focus is on any input/textarea/contenteditable OTHER than the brush
  // slider itself (typing into the slider isn't a thing, but this also
  // covers e.g. the file input's hidden text state and any future form
  // field elsewhere on the page).
  _onKeydown(e) {
    if (e.key !== '[' && e.key !== ']') return;
    if (!this._paintMode || !this.$brushSlider) return;

    const active = document.activeElement;
    const isTypingElsewhere =
      active &&
      active !== this.$brushSlider &&
      (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable);
    if (isTypingElsewhere) return;

    e.preventDefault();

    // A bit larger than the slider's own 0.01 step so a keypress feels like
    // a deliberate size change, not a rounding error.
    const step = 0.05;
    const current = Number(this.$brushSlider.value);
    const from = Number.isFinite(current) ? current : fractionToSlider(BRUSH_DEFAULT_FRACTION);
    const next = Math.min(1, Math.max(0, from + (e.key === ']' ? step : -step)));

    this.$brushSlider.value = String(next);
    this._onBrushSliderInput();
  }

  // -----------------------------------------------------------------------
  // Marching-ants outline rendering (replaces the old translucent-tint
  // overlay entirely — see kiwi-marching-ants-brief.md).
  //
  // Hard constraint driving all of this: no pixel of the photo is ever
  // touched. The overlay canvas only ever draws a stroked (never filled)
  // outline — the selection boundary and the brush-size cursor — both in a
  // fixed black/white dual-colour dashed treatment so they read over any
  // photo, light or dark, unlike the old fixed theme-accent tint.
  //
  // Contours are cached in working-image space (`this._contours`) and are
  // only retraced when the mask itself changes (`_retraceContours` /
  // `_retraceAndRedraw`, called from every mask-mutating call site: enter
  // paint mode, use-whole-photo, undo, clear-all, and stroke-end). The
  // rAF animation loop (`_onAntsFrame`) never retraces — it only advances
  // `lineDashOffset` and re-strokes the cached contour paths, so it stays
  // cheap regardless of mask complexity (brief 3.5, acceptance criteria).
  // -----------------------------------------------------------------------

  // Retraces `this._contours` from the current mask. Pure recompute, no
  // drawing/animation side effects — call `_retraceAndRedraw()` from event
  // handlers instead unless you specifically want retrace without a redraw.
  _retraceContours() {
    if (!this._mask || !this._workingWidth || !this._workingHeight) {
      this._contours = [];
      return;
    }
    this._contours = traceMaskContours(this._mask, this._workingWidth, this._workingHeight, {
      tolerance: 1,
      maxContours: 50,
    });
  }

  // The single entry point every mask-mutating call site should use:
  // retrace the vector contours from the (new) mask, then let
  // `_ensureAntsLoop` decide whether to (re)start the animation loop and
  // draw the first frame immediately so there's no visible lag.
  _retraceAndRedraw() {
    this._retraceContours();
    this._ensureAntsLoop();
  }

  _prefersReducedMotion() {
    if (!this._reducedMotionQuery && window.matchMedia) {
      this._reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    }
    // Read `.matches` live off the cached MediaQueryList (cheap) rather than
    // caching the boolean, so a mid-session OS-setting change is picked up
    // the next time we're about to (re)start the loop, without needing a
    // dedicated 'change' listener.
    return this._reducedMotionQuery ? this._reducedMotionQuery.matches : false;
  }

  // Starts or stops the rAF loop to match current conditions, and always
  // draws one frame immediately (static or animated) so state changes
  // (retrace, paint-mode toggle, tab visibility, reduced-motion) show up
  // right away rather than waiting for the next tick.
  _ensureAntsLoop() {
    const shouldAnimate =
      this._paintMode &&
      this._contours.length > 0 &&
      !document.hidden &&
      !this._prefersReducedMotion();

    if (!shouldAnimate) {
      this._stopAntsLoop();
      this._drawAntsFrame();
      return;
    }

    this._drawAntsFrame();
    if (this._antsRafId === null) {
      this._antsLastTick = 0;
      this._antsRafId = requestAnimationFrame(this._onAntsFrame);
    }
  }

  _stopAntsLoop() {
    if (this._antsRafId !== null) {
      cancelAnimationFrame(this._antsRafId);
      this._antsRafId = null;
    }
  }

  // rAF fires at display refresh rate, but the brief asks for the classic
  // ~15fps "chunky" marching-ants look (and battery savings on mobile), so
  // this throttles actual redraws/offset-advances to ~67ms rather than
  // drawing on every callback.
  _onAntsFrame(timestamp) {
    const shouldContinue =
      this._paintMode &&
      this._contours.length > 0 &&
      !document.hidden &&
      !this._prefersReducedMotion();

    if (!shouldContinue) {
      // Stop entirely rather than no-op-and-reschedule — acceptance
      // criteria requires no rAF running once there's no selection or
      // paint mode is inactive.
      this._antsRafId = null;
      return;
    }

    if (!this._antsLastTick) this._antsLastTick = timestamp;
    const elapsed = timestamp - this._antsLastTick;

    if (elapsed >= 67) {
      this._antsLastTick = timestamp;
      this._antsDashOffset = (this._antsDashOffset + 1) % 10; // dash cycle is 5 on/5 off
      this._drawAntsFrame();
    }

    this._antsRafId = requestAnimationFrame(this._onAntsFrame);
  }

  // Theme accent colour as an "r, g, b" string (kiwi-brush-trail-and-sizing
  // brief, Part 1 tint). The marching-ants rework deleted the old
  // `_accentRgb()` helper along with the resting-state tint it fed — this
  // reintroduces the same lookup for the new, stroke-scoped tint, which is
  // a different feature (see the file-level notes above `_drawAntsFrame`).
  // Falls back to this section's own CTA-button colour (#3d3b74) if the
  // custom property is unexpectedly empty.
  _accentRgb() {
    if (!this._cachedAccentRgb) {
      const raw = getComputedStyle(this).getPropertyValue('--color-scheme-accent-1').trim();
      this._cachedAccentRgb = raw || '61, 59, 116';
    }
    return this._cachedAccentRgb;
  }

  _brushCursorShouldShow() {
    return (
      this._paintMode &&
      this._hoverVisible &&
      this._hoverDisplayX !== null &&
      this._hoverDisplayY !== null
    );
  }

  // Records the pointer's DISPLAY-space (CSS px, relative to the overlay
  // box) position for the brush-size cursor, and whether it should be
  // visible right now: on mouse it follows hover as well as drag (brief
  // 3.4 — brush size should be visible before committing to a stroke); on
  // touch there's no hover concept, so it only shows while a stroke
  // (`this._activePointerId`) is actually active.
  _updateBrushCursorFromEvent(e) {
    const rect = this.$paintOverlay.getBoundingClientRect();
    this._hoverDisplayX = e.clientX - rect.left;
    this._hoverDisplayY = e.clientY - rect.top;
    this._pointerType = e.pointerType;
    this._hoverVisible = e.pointerType === 'mouse' || this._activePointerId !== null;
  }

  _onPaintPointerLeave(e) {
    if (e.pointerId === this._activePointerId) return; // still mid-stroke, e.g. pointer capture edge case
    this._hoverVisible = false;
    this._drawAntsFrame();
  }

  // Resizes a canvas to DISPLAY resolution × devicePixelRatio (shared by
  // both overlay canvases — see the compositing-architecture note above —
  // so their sizing logic can't silently drift apart over time). Returns
  // the 2D context (already reset to the identity transform) plus the
  // CSS-px display dimensions and dpr, all of which every caller needs.
  _resizeCanvasForDisplay(canvas) {
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = Math.max(1, rect.width);
    const displayHeight = Math.max(1, rect.height);
    const pixelWidth = Math.max(1, Math.round(displayWidth * dpr));
    const pixelHeight = Math.max(1, Math.round(displayHeight * dpr));

    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }

    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    return { ctx, displayWidth, displayHeight, dpr };
  }

  // Builds the transform-options object every working<->display coordinate
  // helper in this file expects, for the given display dimensions.
  _transformOpts(displayWidth, displayHeight) {
    return {
      workingWidth: this._workingWidth,
      workingHeight: this._workingHeight,
      naturalWidth: this.$photoPreview.naturalWidth || this._workingWidth,
      naturalHeight: this.$photoPreview.naturalHeight || this._workingHeight,
      displayWidth,
      displayHeight,
    };
  }

  // Builds the brush-size cursor as a device-pixel-space Path2D circle,
  // centred at the given DISPLAY-space (CSS px) point. Shared by the ants
  // canvas (idle-hover cursor + true-size slider preview) and the trail
  // canvas (active-stroke cursor) so the circle-drawing math lives in one
  // place (brief 3.4's math, reused rather than duplicated).
  _buildCursorPath(displayX, displayY, transformOpts, dpr) {
    const scale = workingToDisplayScale(transformOpts) * dpr;
    const radius = Math.max(1, this._brushRadius * scale);
    const path = new Path2D();
    path.arc(displayX * dpr, displayY * dpr, radius, 0, Math.PI * 2);
    return path;
  }

  // The dual black/white dashed-stroke technique (brief 3.2 — mandatory,
  // not a nicety: a single colour disappears wherever it crosses a
  // similarly-toned part of the photo). Shared by every dashed-circle/
  // -contour draw in this file — ants contours, both cursor states, and the
  // true-size slider preview — so it can't drift into inconsistent styling
  // across canvases.
  _strokeDualDashed(ctx, paths, dpr) {
    if (paths.length === 0) return;

    const lineWidth = 1.5 * dpr; // brief 3.2: scale by DPR so it isn't hairline-thin on a phone
    const dashLen = 5 * dpr; // scaled with lineWidth so dash length reads consistently across DPR
    const offset = this._antsDashOffset * dpr;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = lineWidth;
    ctx.setLineDash([dashLen, dashLen]);

    const strokeAll = (color, dashOffset) => {
      ctx.strokeStyle = color;
      ctx.lineDashOffset = -dashOffset;
      paths.forEach((path) => ctx.stroke(path));
    };

    // Second pass offset by half the dash cycle so black and white dashes
    // alternate rather than overlap.
    strokeAll('#000', offset);
    strokeAll('#fff', offset + dashLen);
  }

  // ---------------------------------------------------------------------
  // Compositing architecture (kiwi-brush-trail-and-sizing-brief.md, Part 1):
  // two overlay canvases stack in DOM order photo -> ants -> trail, so the
  // trail always paints above the ants without a full 3-canvas system:
  //
  //   ants canvas ($paintOverlay, `_drawAntsFrame`): the committed-selection
  //     contours (always) + the brush-size cursor, but ONLY when there is
  //     no active stroke (mouse hover with no drag) — plus the true-size
  //     slider preview circle, which also only ever shows while no stroke
  //     is active (adjusting the slider and painting are mutually
  //     exclusive), so it lives here rather than adding a third state to
  //     the trail canvas.
  //   trail canvas ($strokeTrail, `_drawTrailFrame`): the in-progress
  //     stroke tint + the brush-size cursor, but ONLY while a stroke IS
  //     active.
  //
  // Composite order in both states is therefore always photo -> ants(+idle
  // cursor OR preview) -> trail(+active-stroke cursor), matching the
  // brief's "photo -> committed-selection ants -> current-stroke tint ->
  // brush circle cursor" (cursor topmost in whichever of its two states
  // applies).
  // ---------------------------------------------------------------------

  // Draws the cached contours (`this._contours`) plus, when appropriate,
  // the brush-size cursor or the true-size slider preview — see the
  // compositing-architecture note above. Never draws the in-progress
  // stroke tint; that lives entirely on the trail canvas.
  _drawAntsFrame() {
    const canvas = this.$paintOverlay;
    if (!canvas || canvas.classList.contains('hidden')) return;

    const { ctx, displayWidth, displayHeight, dpr } = this._resizeCanvasForDisplay(canvas);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const showPreview = this._brushPreviewActive && this._brushRadius > 0;
    const showHoverCursor =
      !showPreview && this._activePointerId === null && this._brushCursorShouldShow() && this._brushRadius > 0;

    if (this._contours.length === 0 && !showPreview && !showHoverCursor) return;

    const transformOpts = this._transformOpts(displayWidth, displayHeight);

    // Coordinates are built directly in device-pixel space (display-space
    // point × dpr) rather than via ctx.scale/setTransform, so lineWidth and
    // the dash pattern are scaled by dpr explicitly and unambiguously.
    const toDevicePoint = (wx, wy) => {
      const d = mapWorkingToDisplayCoords({ ...transformOpts, x: wx, y: wy });
      return { x: d.x * dpr, y: d.y * dpr };
    };

    const contourPaths = this._contours.map((poly) => {
      const path = new Path2D();
      poly.forEach((pt, i) => {
        const dp = toDevicePoint(pt.x, pt.y);
        if (i === 0) path.moveTo(dp.x, dp.y);
        else path.lineTo(dp.x, dp.y);
      });
      path.closePath();
      return path;
    });

    let cursorPath = null;
    if (showPreview) {
      // "Centred on the image": the display-space centre of the photo box.
      cursorPath = this._buildCursorPath(displayWidth / 2, displayHeight / 2, transformOpts, dpr);
    } else if (showHoverCursor) {
      cursorPath = this._buildCursorPath(this._hoverDisplayX, this._hoverDisplayY, transformOpts, dpr);
    }

    const allPaths = cursorPath ? contourPaths.concat([cursorPath]) : contourPaths;
    this._strokeDualDashed(ctx, allPaths, dpr);
  }

  // Draws the in-progress-stroke tint (diffed live mask vs. the snapshot
  // taken at `pointerdown`) plus the active-stroke brush cursor — see the
  // compositing-architecture note above `_drawAntsFrame`. No-ops (and stays
  // cleared) whenever there is no active stroke.
  _drawTrailFrame() {
    const canvas = this.$strokeTrail;
    if (!canvas || canvas.classList.contains('hidden')) return;

    const { ctx, displayWidth, displayHeight, dpr } = this._resizeCanvasForDisplay(canvas);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (this._activePointerId === null) return; // lifecycle: trail only ever shows mid-stroke

    const transformOpts = this._transformOpts(displayWidth, displayHeight);

    if (this._strokeStartMask && this._mask) {
      this._drawStrokeTint(ctx, transformOpts, dpr);
    }

    if (this._brushCursorShouldShow() && this._brushRadius > 0) {
      const cursorPath = this._buildCursorPath(this._hoverDisplayX, this._hoverDisplayY, transformOpts, dpr);
      this._strokeDualDashed(ctx, [cursorPath], dpr);
    }
  }

  // Renders "pixels this stroke has newly covered" — mask[i]===1 &&
  // strokeStartMask[i]===0 — at ~40% opacity in the theme accent colour
  // (brief Part 1; a different constant from the old removed resting-state
  // tint's ~75%, don't reuse that number). Builds the tint at working-image
  // resolution on a small reusable offscreen canvas, then scales it into
  // display space with a single drawImage — cheaper than per-pixel ops on
  // the (potentially much larger) display-resolution trail canvas, and
  // reuses the same object-fit:cover transform as the ants contours so it
  // lines up pixel-for-pixel with the photo underneath.
  _drawStrokeTint(ctx, transformOpts, dpr) {
    const w = this._workingWidth;
    const h = this._workingHeight;
    if (!w || !h) return;

    if (!this._trailMaskCanvas || this._trailMaskCanvas.width !== w || this._trailMaskCanvas.height !== h) {
      this._trailMaskCanvas = document.createElement('canvas');
      this._trailMaskCanvas.width = w;
      this._trailMaskCanvas.height = h;
    }

    const mctx = this._trailMaskCanvas.getContext('2d');
    mctx.clearRect(0, 0, w, h);
    const imageData = mctx.createImageData(w, h);
    const data = imageData.data;

    const accent = this._accentRgb();
    const parts = accent.split(',').map((n) => parseInt(n.trim(), 10) || 0);
    const [r, g, b] = parts;
    const alpha = Math.round(0.4 * 255); // brief: ~40% opacity, distinct from the old removed tint's ~75%

    const mask = this._mask;
    const startMask = this._strokeStartMask;
    for (let i = 0; i < w * h; i++) {
      if (mask[i] === 1 && startMask[i] === 0) {
        const o = i * 4;
        data[o] = r;
        data[o + 1] = g;
        data[o + 2] = b;
        data[o + 3] = alpha;
      }
    }
    mctx.putImageData(imageData, 0, 0);

    const topLeft = mapWorkingToDisplayCoords({ ...transformOpts, x: 0, y: 0 });
    const bottomRight = mapWorkingToDisplayCoords({ ...transformOpts, x: w, y: h });
    const dx = topLeft.x * dpr;
    const dy = topLeft.y * dpr;
    const dw = (bottomRight.x - topLeft.x) * dpr;
    const dh = (bottomRight.y - topLeft.y) * dpr;

    ctx.imageSmoothingEnabled = false; // crisp brush edges, no blur/bleed across the boundary
    ctx.drawImage(this._trailMaskCanvas, 0, 0, w, h, dx, dy, dw, dh);
  }

  // Clears the trail canvas outright (`ctx.clearRect` the whole thing).
  // Called on stroke end (`pointerup`/`pointercancel`) BEFORE the ants
  // retrace, so there's never a frame where both a stale tint and the
  // freshly-retraced ants are visible at once (brief Part 1 lifecycle) —
  // and defensively from every mask-mutating call site that isn't already
  // guaranteed to be mid-stroke.
  _clearTrailCanvas() {
    const canvas = this.$strokeTrail;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  }

  _paintCircle(cx, cy, radius) {
    const w = this._workingWidth;
    const h = this._workingHeight;
    const r2 = radius * radius;
    const minX = Math.max(0, Math.floor(cx - radius));
    const maxX = Math.min(w - 1, Math.ceil(cx + radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxY = Math.min(h - 1, Math.ceil(cy + radius));

    for (let y = minY; y <= maxY; y++) {
      const dy = y - cy;
      for (let x = minX; x <= maxX; x++) {
        const dx = x - cx;
        if (dx * dx + dy * dy <= r2) this._mask[y * w + x] = 1;
      }
    }
  }

  // Round brush, round joins: stamp overlapping circles along the segment
  // between pointer events so a fast stroke doesn't come out dotted.
  _strokeTo(fromX, fromY, toX, toY) {
    const dx = toX - fromX;
    const dy = toY - fromY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const step = Math.max(1, this._brushRadius / 3);
    const steps = Math.max(1, Math.ceil(dist / step));

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      this._paintCircle(fromX + dx * t, fromY + dy * t, this._brushRadius);
    }
  }

  _workingCoordsFromEvent(e) {
    const rect = this.$paintOverlay.getBoundingClientRect();
    return mapDisplayToWorkingCoords({
      displayX: e.clientX - rect.left,
      displayY: e.clientY - rect.top,
      displayWidth: rect.width,
      displayHeight: rect.height,
      naturalWidth: this.$photoPreview.naturalWidth || this._workingWidth,
      naturalHeight: this.$photoPreview.naturalHeight || this._workingHeight,
      workingWidth: this._workingWidth,
      workingHeight: this._workingHeight,
    });
  }

  // Regenerate-on-stroke-end (colour-quality brief item 3's own fallback):
  // measured k-means cost at the 500px working resolution this section now
  // uses (~250k pixels) is ~0.3s at k=5 but ~1-1.5s at k=8-15 on a fast
  // desktop CPU — on a mid-range phone that's easily several seconds. Firing
  // a full re-cluster on a ~150ms cadence *while the pointer is still
  // moving* would visibly queue up and lag behind the brush at the higher
  // end of the (now up to 15) colour-count range. So live regeneration
  // during an active stroke is intentionally NOT scheduled here any more —
  // painting itself (the mask + overlay redraw) stays instant regardless of
  // k, and the one authoritative, guaranteed-fresh recompute happens at
  // stroke end (`_onPaintPointerEnd`), matching the brief's explicit
  // instruction to move the trigger rather than drop the resolution back
  // down. Kept as a small wrapper (rather than inlining the timeout) so a
  // future debounced-during-stroke experiment has one place to change.
  _scheduleRegenerate() {
    clearTimeout(this._regenerateTimer);
  }

  _onPaintPointerDown(e) {
    // kiwi-mobile-overhaul-fix: this canvas's own `pointer-events: none`
    // default (see palette-from-photo.liquid) should already stop events
    // from reaching here at all while paint mode is inactive — this guard
    // is a second, independent layer that doesn't depend on that CSS
    // (real-device report: without either, a touch here after Done was
    // silently painting into the mask and corrupting an already-finished
    // selection — the CSS wasn't actually taking effect, see the comment
    // there for why).
    if (!this._paintMode) return;
    if (this._activePointerId !== null) return; // ignore multi-touch
    this._activePointerId = e.pointerId;
    try {
      this.$paintOverlay.setPointerCapture(e.pointerId);
    } catch (err) {
      // Some browsers throw if the pointer isn't recognised as active
      // (seen with synthetic events; can also happen on real devices in
      // edge cases) — painting still works fine without capture, it just
      // won't survive the pointer leaving the canvas as gracefully.
    }

    // Stroke-level undo: push a copy of the mask as it was before this
    // stroke (cap 10, drop oldest). Also doubles as the Part 1 trail's
    // stroke-start snapshot (the brief's own suggestion — reuse the value
    // just pushed rather than taking a second, redundant slice()).
    this._undoStack.push(this._mask.slice());
    if (this._undoStack.length > 10) this._undoStack.shift();
    this._strokeStartMask = this._undoStack[this._undoStack.length - 1];

    const { x, y } = this._workingCoordsFromEvent(e);
    this._lastPaintX = x;
    this._lastPaintY = y;
    this._paintCircle(x, y, this._brushRadius);

    // Marching-ants timing (brief 3.5): do NOT retrace on pointerdown — the
    // ants keep showing the pre-stroke outline for the whole stroke, only
    // the brush-cursor circle (updated here) gives live feedback while
    // painting. `_drawAntsFrame()` redraws the cached (still pre-stroke)
    // contours (no cursor — the stroke is now active, so the cursor moved
    // to the trail canvas, see the compositing note above `_drawAntsFrame`);
    // `_drawTrailFrame()` draws the first bit of tint (there's already
    // something to show since `_paintCircle` above already ran once) plus
    // the cursor.
    this._updateBrushCursorFromEvent(e);
    this._drawAntsFrame();
    this._drawTrailFrame();
    this._scheduleRegenerate();
  }

  _onPaintPointerMove(e) {
    // Brush-cursor tracking is independent of an active stroke: on mouse it
    // follows hover as well as drag (brief 3.4), so this runs even when
    // `this._activePointerId` is null. The mask-painting logic below stays
    // gated on an active stroke exactly as before.
    this._updateBrushCursorFromEvent(e);

    if (e.pointerId !== this._activePointerId) {
      this._drawAntsFrame(); // just move the cursor; no retrace, no paint
      return;
    }

    const coalesced =
      typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents() : null;
    const events = coalesced && coalesced.length ? coalesced : [e];

    events.forEach((ev) => {
      const { x, y } = this._workingCoordsFromEvent(ev);
      this._strokeTo(this._lastPaintX, this._lastPaintY, x, y);
      this._lastPaintX = x;
      this._lastPaintY = y;
    });

    // Do NOT retrace contours here (brief 3.5 — marching squares over the
    // full mask on every pointermove would be jank on mobile). `_drawAntsFrame`
    // just repaints the cached pre-stroke contours (no cursor, mid-stroke —
    // see the compositing note above it); `_drawTrailFrame` redraws the
    // live mask-vs-snapshot diff (growing tint) plus the cursor at its new
    // position.
    this._drawAntsFrame();
    this._drawTrailFrame();
    this._scheduleRegenerate();
  }

  _onPaintPointerEnd(e) {
    if (e.pointerId !== this._activePointerId) return;
    this._activePointerId = null;
    clearTimeout(this._regenerateTimer);

    // Trail lifecycle (brief Part 1): clear the tint BEFORE retracing the
    // ants, so there's never a frame where both the stale tint and the
    // fresh ants are visible together. Same path for pointercancel — no
    // stranded tint is allowed to survive either.
    this._clearTrailCanvas();
    this._strokeStartMask = null;

    // Touch has no hover concept — the cursor should disappear once the
    // stroke ends. Mouse keeps showing it (still hovering the canvas).
    if (this._pointerType !== 'mouse') this._hoverVisible = false;

    // Stroke-end: the one point in the pointer lifecycle where contours are
    // retraced and the ants updated (brief 3.5).
    this._retraceAndRedraw();

    // Stroke-end: authoritative regenerate + the one aria-live announcement
    // for this stroke (brief section 9 — not per stroke-tick).
    this._recomputeAndRender({ announce: true });
  }

  _showGuard(message) {
    if (!this.$guard) return;
    this.$guard.textContent = message;
    this.$guard.classList.remove('hidden');
    this.$swatchList.innerHTML = '';
    this.$addToCart.disabled = true;
    this._setCtaDetails(null, '');
    this.$results.classList.remove('hidden');
  }

  _clearGuard() {
    if (!this.$guard) return;
    this.$guard.textContent = '';
    this.$guard.classList.add('hidden');
  }

  // ---------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------
  _renderResults() {
    const { srgbToLab } = this._paletteMath;
    this.$swatchList.innerHTML = '';

    // How many times each product appears across the whole palette (for the
    // "×N" duplicate badge — kiwi-allow-duplicates brief section 3) and, by
    // variant id, the deduplicated set actually charged for (brief: "aggregate
    // by variant ID" — two slots with the same colour but a DIFFERENT
    // selected size are genuinely two purchases, not a duplicate).
    const productCounts = new Map();
    this._currentMatches.forEach((m) => {
      productCounts.set(m.product.id, (productCounts.get(m.product.id) || 0) + 1);
    });
    const uniqueVariants = new Map(); // variant.id -> variant
    let slotsWithVariant = 0;

    this._currentMatches.forEach((match, index) => {
      const { product } = match;
      const variant = this._selectedVariantFor(product);
      if (variant) {
        slotsWithVariant += 1;
        if (!uniqueVariants.has(variant.id)) uniqueVariants.set(variant.id, variant);
      }

      const li = document.createElement('li');
      li.className = 'palette-builder__swatch-card';
      li.dataset.productId = product.id;

      const block = document.createElement('div');
      block.className = 'palette-builder__swatch-block';
      block.style.backgroundColor = product.hex;

      // Contrast-check: compute Lab lightness of the swatch and switch
      // between dark/light text for anything placed directly on it.
      const lab = product.lab || srgbToLab(...(hexToRgbTuple(product.hex) || [0, 0, 0]));
      const textColor = lab.L > 60 ? '#111111' : '#ffffff';

      const hexLabel = document.createElement('span');
      hexLabel.className = 'palette-builder__swatch-hex-label';
      hexLabel.style.color = textColor;
      hexLabel.textContent = product.hex.toUpperCase();
      block.appendChild(hexLabel);

      // Duplicate marker (kiwi-allow-duplicates brief section 3): "a small
      // '×2' badge... enough that the customer can see why five swatches
      // cost four bottles." Shows the right multiplier for THIS product
      // specifically (e.g. a colour used 3 times shows "×3" on all three of
      // its cards), computed once per render via `productCounts` above.
      const occurrenceCount = productCounts.get(product.id) || 1;
      if (occurrenceCount > 1) {
        const dupBadge = document.createElement('span');
        dupBadge.className = 'palette-builder__duplicate-badge';
        dupBadge.style.color = textColor;
        dupBadge.textContent = `×${occurrenceCount}`;
        dupBadge.setAttribute(
          'aria-label',
          `${product.title} appears ${occurrenceCount} times in this palette`
        );
        block.appendChild(dupBadge);
      }

      if (this._config.showSourceSwatch) {
        const sourceChip = document.createElement('span');
        sourceChip.className = 'palette-builder__source-chip';
        // Representative pixel (colour-quality brief item 1), not the
        // centroid — this is "the actual colour extracted from your photo",
        // and the centroid can be a washed-out mean that appears nowhere in
        // the image.
        sourceChip.style.backgroundColor = labToCss(match.cluster.representative, this._paletteMath);
        sourceChip.title = 'Colour matched from your image';
        sourceChip.setAttribute('aria-hidden', 'true');
        block.appendChild(sourceChip);
      }

      const dismissBtn = document.createElement('button');
      dismissBtn.type = 'button';
      dismissBtn.className = 'palette-builder__dismiss-btn';
      dismissBtn.setAttribute('aria-label', `Remove ${product.title} from palette`);
      dismissBtn.textContent = '×';
      dismissBtn.addEventListener('click', () => this._dismissSwatch(index));
      block.appendChild(dismissBtn);

      const nameLink = document.createElement('a');
      nameLink.href = product.url;
      nameLink.className = 'font-medium text-sm underline';
      nameLink.textContent = product.title;

      const meta = document.createElement('p');
      meta.className = 'text-xs opacity-70';
      meta.textContent = variant
        ? `${formatMoney(variant.price, this._config.moneyFormat)} · ${
            variant.available ? 'In stock' : 'Sold out'
          }`
        : 'Not available';

      li.append(block, nameLink, meta);

      if (product.variants && product.variants.length > 1) {
        const sizeSelect = document.createElement('select');
        sizeSelect.className = 'palette-builder__size-select';
        sizeSelect.setAttribute('aria-label', `Size for ${product.title}`);
        product.variants.forEach((v) => {
          const option = document.createElement('option');
          option.value = String(v.id);
          option.textContent = v.available ? v.title : `${v.title} (sold out)`;
          option.disabled = !v.available;
          if (variant && v.id === variant.id) option.selected = true;
          sizeSelect.appendChild(option);
        });
        sizeSelect.addEventListener('change', () => {
          const chosen = product.variants.find((v) => String(v.id) === sizeSelect.value);
          if (chosen) {
            this._variantSelections.set(product.id, chosen);
            this._renderResults();
          }
        });
        li.appendChild(sizeSelect);
      }

      // Near-miss alternatives (colour-quality brief item 5): kept quiet by
      // default — a small text toggle, revealed on tap, never competing
      // visually with the palette itself.
      if (match.rankedCandidates && match.rankedCandidates.length > 0) {
        const isOpen = this._revealedAlternativesFor.has(match);

        const altToggle = document.createElement('button');
        altToggle.type = 'button';
        altToggle.className = 'palette-builder__alt-toggle text-xs underline';
        altToggle.textContent = isOpen ? 'Hide alternatives' : 'Not quite right?';
        altToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        altToggle.addEventListener('click', () => {
          if (this._revealedAlternativesFor.has(match)) {
            this._revealedAlternativesFor.delete(match);
          } else {
            this._revealedAlternativesFor.add(match);
          }
          this._renderResults();
        });

        const altList = document.createElement('div');
        altList.className = 'palette-builder__alt-list';
        altList.classList.toggle('hidden', !isOpen);
        altList.setAttribute('aria-label', `Alternative colours for ${product.title}`);

        match.rankedCandidates.forEach((alt) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'palette-builder__alt-chip';
          chip.setAttribute('aria-label', `Swap in ${alt.product.title}`);

          const swatchDot = document.createElement('span');
          swatchDot.className = 'palette-builder__alt-chip-swatch';
          swatchDot.style.backgroundColor = alt.product.hex;
          swatchDot.setAttribute('aria-hidden', 'true');

          const label = document.createElement('span');
          label.className = 'palette-builder__alt-chip-label';
          label.textContent = alt.product.title;

          chip.append(swatchDot, label);
          chip.addEventListener('click', () => this._swapAlternativeIntoSlot(index, alt.product));
          altList.appendChild(chip);
        });

        li.append(altToggle, altList);
      }

      this.$swatchList.appendChild(li);
    });

    this.$results.classList.remove('hidden');
    this.$addToCart.disabled = this._currentMatches.length === 0;

    // Total is what actually gets charged: each unique variant's price
    // counted exactly once (kiwi-allow-duplicates brief section 3), not
    // once per slot. Line 2 always shows the unique bottle count and total
    // (kiwi-cart-button brief) — never only in the duplicate case, so it
    // reads as information rather than an exception, and the button's
    // height never changes based on which case is active.
    let total = 0;
    uniqueVariants.forEach((variant) => {
      total += Number(variant.price) || 0;
    });
    const moneyText = formatMoney(total, this._config.moneyFormat);
    this._setCtaDetails(uniqueVariants.size, moneyText);
  }

  // Line 2 text + the combined aria-label live together: without a single
  // aria-label spanning both lines, a screen reader announces them as two
  // disconnected fragments (kiwi-cart-button brief, Accessibility).
  _setCtaDetails(count, moneyText) {
    const baseLabel = this._config.ctaText;
    if (count == null) {
      this.$ctaTotal.textContent = '';
      this.$addToCart.setAttribute('aria-label', baseLabel);
      return;
    }
    const bottleWord = count === 1 ? 'bottle' : 'bottles';
    this.$ctaTotal.textContent = `${count} ${bottleWord} · ${moneyText}`;
    this.$addToCart.setAttribute('aria-label', `${baseLabel}, ${count} ${bottleWord}, ${moneyText}`);
  }

  _dismissSwatch(index) {
    const [removed] = this._currentMatches.splice(index, 1);
    if (removed) this._revealedAlternativesFor.delete(removed);
    this._renderResults();
    this._announce('Colour removed from palette.');
  }

  // ---------------------------------------------------------------------
  // Near-miss alternatives (colour-quality brief item 5): tapping an
  // alternative chip swaps it into that slot and ONLY that slot (kiwi-allow-
  // duplicates brief section 1: "a direct action should have exactly one
  // effect"). Duplicates are allowed — if the tapped candidate is already
  // used elsewhere in the palette, nothing is swapped away to avoid it; the
  // customer chose it, so two slots holding the same colour is a valid
  // result now, not an error to silently correct.
  // ---------------------------------------------------------------------
  _swapAlternativeIntoSlot(slotIndex, candidateProduct) {
    const thisMatch = this._currentMatches[slotIndex];
    if (!thisMatch || thisMatch.product.id === candidateProduct.id) return;

    thisMatch.product = candidateProduct;
    this._updateMatchAfterProductChange(thisMatch);
    this._revealedAlternativesFor.delete(thisMatch);

    // A swapped-in product should get its own default variant, not inherit
    // whatever the previous product's slot had selected — `_selectedVariantFor`
    // is keyed by product id and lazily defaults any id with no existing
    // selection, so no explicit reset is needed here; each product keeps
    // (or gets, if new to the palette) its own default.
    this._renderResults();
    this._announce(`Swapped in ${candidateProduct.title}.`);
    this._track('palette_alternative_swapped', { product_id: candidateProduct.id });
  }

  // Keeps a match's cost-derived fields (distance, rankedCandidates) in
  // sync after its `product` changes via a swap — `costRow` travels with
  // the match itself, so this recompute stays correct regardless of any
  // earlier dismiss having shifted slot indices around.
  _updateMatchAfterProductChange(match) {
    const assignedIdx = this._currentCandidates.findIndex((p) => p.id === match.product.id);
    if (assignedIdx === -1 || !match.costRow) return;
    match.distance = match.costRow[assignedIdx];
    match.rankedCandidates = rankCandidatesForRow(match.costRow, this._currentCandidates, assignedIdx).slice(0, 3);
  }

  // ---------------------------------------------------------------------
  // Add to cart
  // ---------------------------------------------------------------------
  async _addPaletteToCart() {
    if (this._addingToCart || this._currentMatches.length === 0) return;
    this._addingToCart = true;
    this._clearCartError();
    this.$addToCart.disabled = true;

    // Aggregate by variant ID before building the payload (kiwi-allow-
    // duplicates brief section 3): a palette of 5 with the same colour (and
    // same selected size) in two slots is 5 slots but 4 purchases. Two slots
    // with the same colour but a DIFFERENT selected size are genuinely two
    // different purchases and stay separate entries — this dedupes on
    // variant id, not product id. Done client-side rather than relying on
    // Shopify's own line-merging, since `_palette_source` is a line item
    // property and can cause visually-identical variants to land on
    // separate cart lines anyway.
    const variantsById = new Map(); // variant.id -> { variant, product }
    this._currentMatches.forEach((match) => {
      const variant = this._selectedVariantFor(match.product);
      if (!variant) return;
      if (!variantsById.has(variant.id)) {
        variantsById.set(variant.id, { variant, product: match.product });
      }
    });

    // `_palette_size` (kiwi-mobile-and-tracking brief, Part 2) is the
    // customer's chosen palette size — this._currentMatches.length, the
    // slot count — not the deduplicated purchase count. Same value on
    // every item in one add-to-cart action; both properties are `_`-
    // prefixed so they're hidden from the customer at checkout but persist
    // on the order permanently, visible in admin and CSV export.
    const paletteSize = String(this._currentMatches.length);
    const items = Array.from(variantsById.values()).map(({ variant }) => ({
      id: variant.id,
      // One unique colour = quantity 1, regardless of how many slots
      // reference it (brief's explicit recommendation) — this is true
      // dedup, not merging duplicate slots into a multi-quantity line.
      quantity: 1,
      properties: { _palette_source: 'photo-palette-builder', _palette_size: paletteSize },
    }));

    if (items.length === 0) {
      this._showCartError("None of these colours have a purchasable size right now.");
      this._addingToCart = false;
      this.$addToCart.disabled = false;
      return;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (response.ok) {
        const value = Array.from(variantsById.values()).reduce(
          (sum, { variant }) => sum + (Number(variant.price) || 0) / 100,
          0
        );
        this._onCartAddSuccess(value, items.length);
        return;
      }

      // Bulk add failed — Shopify's /cart/add.js with an `items` array is
      // all-or-nothing, so retry one at a time to find out which specific
      // colour failed (e.g. sold out between page load and click) and add
      // the rest anyway.
      await this._addItemsIndividually(items, variantsById);
    } catch (err) {
      console.error('[palette-photo-builder] cart add network error', err);
      this._showCartError(
        "We couldn't reach the cart just now (network error). Please check your connection and try again."
      );
      document.dispatchEvent(
        new CustomEvent('theme:product:error:add-to-cart', {
          detail: { message: 'Palette builder: network error adding to cart' },
        })
      );
    } finally {
      this._addingToCart = false;
      this.$addToCart.disabled = false;
    }
  }

  // `items` is already deduplicated by variant id (see `_addPaletteToCart`),
  // so it no longer lines up index-for-index with `this._currentMatches`
  // (which stays one entry per slot). `variantsById` (variant id -> {variant,
  // product}) is passed through so failures can be named by looking up the
  // variant id directly, rather than assuming `items[i]` corresponds to
  // `this._currentMatches[i]`.
  async _addItemsIndividually(items, variantsById) {
    const failed = [];
    let addedCount = 0;
    let addedValue = 0;

    for (const item of items) {
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item),
        });
        if (res.ok) {
          const body = await res.json();
          addedCount += 1;
          addedValue += Number(body.price || 0) / 100;
        } else {
          const errBody = await res.json().catch(() => ({}));
          failed.push({ item, message: errBody.description || errBody.message });
        }
      } catch (err) {
        failed.push({ item, message: 'network error' });
      }
    }

    if (addedCount > 0) {
      this._onCartAddSuccess(addedValue, addedCount, { partial: failed.length > 0 });
    }

    if (failed.length > 0) {
      const names = failed
        .map((f) => {
          const entry = variantsById.get(f.item.id);
          return entry ? entry.product.title : 'One colour';
        })
        .join(', ');
      this._showCartError(
        `${names} ${failed.length === 1 ? "couldn't" : "could not"} be added (likely sold out). ` +
          (addedCount > 0
            ? 'The rest of your palette was added to your cart.'
            : 'Nothing was added — please try again.')
      );
      document.dispatchEvent(
        new CustomEvent('theme:product:error:add-to-cart', {
          detail: { message: `Palette builder: failed to add ${names}` },
        })
      );
    }
  }

  // `count` is the number of UNIQUE colours actually added (deduplicated by
  // variant id — kiwi-allow-duplicates brief section 4: "decide whether it
  // logs slot count or unique count, pick one and be consistent" — this
  // event reflects what was actually added to the cart, so unique count is
  // the honest choice here, unlike `palette_generated` which logs slot count
  // since it fires before any manual swap, when the two are identical anyway).
  _onCartAddSuccess(value, count) {
    this._track('palette_added_to_cart', { value, colour_count: count });
    this._announce('Your palette was added to your cart.');

    // Hook into the theme's own pub/sub cart events (assets/theme-events.js)
    // rather than inventing a new refresh mechanism.
    document.dispatchEvent(new CustomEvent('theme:product:add', { detail: {} }));
    document.dispatchEvent(new CustomEvent('theme:update:cart'));
    document.dispatchEvent(new CustomEvent('theme:open:cart-drawer'));
  }

  // ---------------------------------------------------------------------
  // Misc
  // ---------------------------------------------------------------------
  _reset() {
    this._pixelsLab = null;
    this._pixelsLabFull = null;
    this._workingWidth = 0;
    this._workingHeight = 0;
    this._resetMaskState();
    this._currentMatches = [];
    this._currentCandidates = [];
    this._revealedAlternativesFor.clear();
    this.$results.classList.add('hidden');
    this.$controls.classList.add('hidden');
    this.$fileInput.value = '';
    this._clearError();
    this._clearCartError();
    this._currentCount = this._config.defaultCount;
    this._updateCountButtons();
    this._announce('Start over. Upload a new image.');
  }

  _showError(message) {
    this.$error.textContent = message;
    this.$error.classList.remove('hidden');
    this._announce(message);
  }

  _clearError() {
    this.$error.textContent = '';
    this.$error.classList.add('hidden');
  }

  _showCartError(message) {
    this.$cartError.textContent = message;
    this.$cartError.classList.remove('hidden');
    this._announce(message);
  }

  _clearCartError() {
    this.$cartError.textContent = '';
    this.$cartError.classList.add('hidden');
  }

  _announce(message) {
    if (this.$status) this.$status.textContent = message;
  }

  _track(eventName, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
  }

  disconnectedCallback() {
    if (this._pasteHandler) document.removeEventListener('paste', this._pasteHandler);
    if (this._visibilityHandler) document.removeEventListener('visibilitychange', this._visibilityHandler);
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
    if (this._keydownHandler) document.removeEventListener('keydown', this._keydownHandler);
    if (this._popstateHandler) window.removeEventListener('popstate', this._popstateHandler);
    if (this._fullscreenResizeHandler) window.removeEventListener('resize', this._fullscreenResizeHandler);
    if (this._imageObjectUrl) URL.revokeObjectURL(this._imageObjectUrl);
    clearTimeout(this._regenerateTimer);
    clearTimeout(this._brushPreviewTimer);
    this._stopAntsLoop();
    // Defensive: never leave the page scroll-locked if this element is
    // torn down (e.g. a section re-render) while full-screen paint mode
    // is active.
    if (this._fullscreenActive) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    }
  }
}

// ---------------------------------------------------------------------
// Pure helper functions (kept outside the class so they're easy to test
// or move into palette-math.js later if that ever makes sense).
// ---------------------------------------------------------------------

/**
 * Runs k-means clustering and returns clusters sorted by population
 * descending (brief step 4: "Sort resulting clusters by pixel population").
 * Deliberately a single pure call: (pixels, k, seed) -> clusters, so this
 * exact call could be postMessage'd to a Web Worker later.
 */
function runClustering(kMeansLab, pixelsLab, k, seed) {
  const result = kMeansLab(pixelsLab, k, { seed, maxIterations: 20, restarts: 3 });
  return [...result].sort((a, b) => b.population - a.population);
}

/**
 * Sorts a cost-matrix row (one cluster's distance to every candidate) into
 * ascending "best match first" order, tagging each with the candidate and
 * its distance, and excluding the candidate index currently assigned to
 * this row (the near-miss list is about alternatives, not the pick itself).
 * Ties (equal distance) are broken deterministically by candidate index so
 * the resulting order — and therefore which 3 alternatives are shown — is
 * stable across renders.
 * @param {number[]} row this cluster's cost-matrix row
 * @param {object[]} candidates
 * @param {number} assignedIdx candidate index already assigned to this row
 * @returns {{product: object, distance: number}[]}
 */
function rankCandidatesForRow(row, candidates, assignedIdx) {
  const ranked = [];
  for (let j = 0; j < row.length; j++) {
    if (j === assignedIdx) continue;
    ranked.push({ product: candidates[j], distance: row[j], _idx: j });
  }
  ranked.sort((a, b) => (a.distance - b.distance) || (a._idx - b._idx));
  return ranked.map(({ product, distance }) => ({ product, distance }));
}

/**
 * Near-duplicate dedup (colour-quality brief item 6): after Hungarian
 * assignment, if two clusters were assigned product colours within
 * NEAR_DUPLICATE_DELTA_E of each other, the palette effectively repeats a
 * colour. Push the lower-priority cluster (smaller pixel population — the
 * less dominant colour) to its own next-best still-unused candidate, drawn
 * from that cluster's own cost-matrix row (ascending, skipping any
 * candidate already used elsewhere). Cascades up to MAX_DEDUP_PASSES times;
 * gives up gracefully (console.warn, keep the last assignment) rather than
 * looping forever, which should be very rare with ~50 real candidates.
 *
 * Deliberately compares PRODUCT colours to each other with plain, unweighted
 * CIEDE2000 (no k_L/k_C/k_H) — this is about whether two catalogue colours
 * read as the same colour, not about match quality against a photo.
 *
 * @param {{clusters: object[], candidates: object[], costMatrix: number[][], assignment: number[], deltaE2000: Function}} args
 * @returns {number[]} the (possibly adjusted) assignment
 */
function dedupeNearDuplicateAssignment({ clusters, candidates, costMatrix, assignment, deltaE2000 }) {
  const result = assignment.slice();

  for (let pass = 0; pass < MAX_DEDUP_PASSES; pass++) {
    let conflict = null;

    for (let i = 0; i < result.length && !conflict; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const productA = candidates[result[i]];
        const productB = candidates[result[j]];
        if (!productA || !productB) continue;
        const de = deltaE2000(productA.lab, productB.lab);
        if (de < NEAR_DUPLICATE_DELTA_E) {
          conflict = clusters[i].population <= clusters[j].population ? i : j;
          break;
        }
      }
    }

    if (conflict === null) break;

    const row = costMatrix[conflict];
    // Exclude every candidate currently in use anywhere in the palette,
    // INCLUDING the conflicting row's own current pick — otherwise, since
    // nothing else is "using" that candidate, the search would just
    // re-select the very colour we're trying to move away from and make no
    // progress.
    const used = new Set(result);
    const candidateOrder = row
      .map((cost, idx) => idx)
      .sort((a, b) => (row[a] - row[b]) || (a - b));

    const nextCandidateIdx = candidateOrder.find((idx) => !used.has(idx));

    if (nextCandidateIdx === undefined) {
      console.warn(
        '[palette-photo-builder] Could not resolve a near-duplicate swatch after ' +
          `${pass + 1} pass(es) — leaving the current assignment as-is.`
      );
      break;
    }

    result[conflict] = nextCandidateIdx;
  }

  return result;
}

/**
 * Composes masking with the near-white/near-black extremes filter in one
 * pass (brief section 2's "prerequisite refactor"): a pixel is included
 * when `(!mask || mask[i]) && !isExtreme(px)`. `mask` is `null` for "whole
 * image" — that path must stay byte-identical to the old direct
 * `filterExtremes(pixelsLab, {minL:5, maxL:95})` call it replaces.
 * @param {{L:number,a:number,b:number}[]} pixelsLabFull
 * @param {{mask: Uint8Array|null, ignoreExtremes: boolean}} opts
 * @returns {{L:number,a:number,b:number}[]}
 */
function collectPixels(pixelsLabFull, { mask, ignoreExtremes }) {
  const out = [];
  for (let i = 0; i < pixelsLabFull.length; i++) {
    if (mask && !mask[i]) continue;
    const p = pixelsLabFull[i];
    if (ignoreExtremes && (p.L < 5 || p.L > 95)) continue;
    out.push(p);
  }
  return out;
}

/**
 * Cheap, stable (not cryptographic) hash of a mask, folded into the RNG
 * seed so a changed selection reseeds k-means deterministically (brief
 * section 7: "same photo + same mask + same k = same palette").
 * @param {Uint8Array} mask
 * @returns {number} unsigned 32-bit integer
 */
function hashMask(mask) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < mask.length; i++) {
    hash ^= mask[i];
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Exponential/geometric slider-to-fraction mapping (kiwi-brush-trail-and-
 * sizing-brief.md, Part 2): the slider runs 0-1 internally, and each equal
 * step MULTIPLIES the resulting fraction rather than adding to it, so the
 * slider feels uniform across its whole travel (perceived brush size scales
 * with area, i.e. the square of a linear dimension, which is why a linear
 * slider felt wildly uneven). t=0 -> BRUSH_MIN_FRACTION, t=1 ->
 * BRUSH_MAX_FRACTION, t=0.5 lands at the geometric mean of the two (~7% for
 * this file's constants).
 * @param {number} t 0-1
 * @returns {number} fraction of the image's longest edge
 */
function sliderToFraction(t) {
  return BRUSH_MIN_FRACTION * Math.pow(BRUSH_MAX_FRACTION / BRUSH_MIN_FRACTION, t);
}

/**
 * Exact algebraic inverse of `sliderToFraction`: given a target fraction,
 * solves for the slider position `t` that produces it. Used once, at
 * connect time, to set the slider's initial position from
 * BRUSH_DEFAULT_FRACTION rather than hardcoding a slider value (brief: "so
 * the constants stay the single source of truth"). Clamps the input into
 * [BRUSH_MIN_FRACTION, BRUSH_MAX_FRACTION] first so an out-of-range
 * fraction can't produce a `t` outside [0, 1].
 * @param {number} fraction
 * @returns {number} t, 0-1
 */
function fractionToSlider(fraction) {
  const clamped = Math.min(BRUSH_MAX_FRACTION, Math.max(BRUSH_MIN_FRACTION, fraction));
  return Math.log(clamped / BRUSH_MIN_FRACTION) / Math.log(BRUSH_MAX_FRACTION / BRUSH_MIN_FRACTION);
}

/**
 * Maps a pointer position within the *displayed* image's box (CSS pixels,
 * relative to that box's top-left) to working-image pixel coordinates.
 * Written defensively for `object-fit: cover`: if the displayed box's
 * aspect ratio differs from the natural image's, the visible (post-crop)
 * region is computed first and the mapping only samples inside it — even
 * though in this section the display and working images always share the
 * same source aspect ratio today.
 * @returns {{x:number, y:number}} integer working-image coordinates, clamped
 */
function mapDisplayToWorkingCoords(o) {
  // General object-fit: cover formula: the image is uniformly scaled by
  // whichever ratio makes it fully cover the display box, so the visible
  // (post-crop) region of the natural image is displayWidth/scale by
  // displayHeight/scale, centred. Reduces to a plain uniform scale (no
  // crop offset) whenever the two aspect ratios already match, which is
  // the only case this section actually produces today.
  const scale = Math.max(o.displayWidth / o.naturalWidth, o.displayHeight / o.naturalHeight);
  const visibleWidth = o.displayWidth / scale;
  const visibleHeight = o.displayHeight / scale;
  const naturalX = (o.naturalWidth - visibleWidth) / 2 + (o.displayX / o.displayWidth) * visibleWidth;
  const naturalY = (o.naturalHeight - visibleHeight) / 2 + (o.displayY / o.displayHeight) * visibleHeight;

  return {
    x: Math.min(o.workingWidth - 1, Math.max(0, Math.floor((naturalX / o.naturalWidth) * o.workingWidth))),
    y: Math.min(o.workingHeight - 1, Math.max(0, Math.floor((naturalY / o.naturalHeight) * o.workingHeight))),
  };
}

/**
 * The exact algebraic inverse of `mapDisplayToWorkingCoords`: maps a
 * working-image point (`o.x`, `o.y`) to a DISPLAY-space point (CSS px,
 * relative to the overlay box's top-left). Used by the marching-ants
 * renderer to place contour points back onto the display-space canvas
 * (`_drawAntsFrame`) — deliberately reuses the same object-fit: cover
 * derivation as the forward function rather than duplicating/approximating
 * it, per the brief. Unlike the forward direction, this is used for
 * continuous drawing geometry, not discrete event sampling, so it doesn't
 * floor/clamp to integers.
 * @returns {{x:number, y:number}} display-space (CSS px) coordinates
 */
function mapWorkingToDisplayCoords(o) {
  const scale = Math.max(o.displayWidth / o.naturalWidth, o.displayHeight / o.naturalHeight);
  const visibleWidth = o.displayWidth / scale;
  const visibleHeight = o.displayHeight / scale;

  const naturalX = (o.x / o.workingWidth) * o.naturalWidth;
  const naturalY = (o.y / o.workingHeight) * o.naturalHeight;

  return {
    x: ((naturalX - (o.naturalWidth - visibleWidth) / 2) / visibleWidth) * o.displayWidth,
    y: ((naturalY - (o.naturalHeight - visibleHeight) / 2) / visibleHeight) * o.displayHeight,
  };
}

/**
 * The *scale-only* component of the working->display transform (brief
 * 3.4): for turning a radius (the brush size) into display px, we don't
 * want the crop-offset part of `mapWorkingToDisplayCoords` — just the
 * uniform scale factor working-image px are stretched by to reach display
 * px. Still derived via the same object-fit: cover formula so it stays
 * correct if display/working ever stop sharing an aspect ratio.
 * @returns {number}
 */
function workingToDisplayScale(o) {
  const scale = Math.max(o.displayWidth / o.naturalWidth, o.displayHeight / o.naturalHeight);
  return (o.naturalWidth / o.workingWidth) * scale;
}

// ---------------------------------------------------------------------
// Contour extraction: marching squares + Douglas-Peucker (brief 3.1).
// Plain, standalone, unit-testable functions — mask in, polygons out. Not
// class methods, and no dependency on canvas/DOM, so they can be verified
// with a hand-built Uint8Array mask (see the "square with a hole" test the
// brief itself suggests) before any rendering is wired up.
// ---------------------------------------------------------------------

/**
 * Traces the boundary of the 1-valued region of a binary raster mask into
 * closed polygons, in mask-pixel (working-image) coordinate space.
 *
 * Algorithm: marching squares over the grid of pixel *centres* (so a cell
 * sits between four neighbouring pixels), with the mask implicitly padded
 * by a border of 0s so blobs touching the image edge still close into a
 * proper polygon. Because the mask is binary, thresholded at 0.5, every
 * edge crossing lands exactly at the midpoint between the two samples —
 * there's no need for linear interpolation, though the same 0.5-threshold
 * comparison generalises to a non-binary mask without changes.
 *
 * Cell topology, rather than a hardcoded 16-case lookup table: walk each
 * cell's four corners (TL, TR, BR, BL) clockwise and record every 0->1
 * ("enter") and 1->0 ("leave") transition, in the encountered order (they
 * necessarily alternate around any cyclic 0/1 sequence). Pairing each
 * "enter" with the next "leave" produces one directed segment per crossing
 * pair — this also transparently handles the ambiguous 4-crossing "saddle"
 * cases (corners diagonally 1,0,1,0) by just producing two segments instead
 * of one, with a fixed (encounter-order) resolution of the ambiguity.
 * Adjacent cells necessarily traverse a shared edge in opposite directions,
 * so one cell's "enter" point is the neighbouring cell's "leave" point at
 * the exact same coordinate — chaining segments by matching endpoint
 * therefore stitches correctly across the whole grid into closed loops,
 * with holes and disconnected blobs simply falling out as separate loops.
 *
 * @param {Uint8Array} mask working-resolution mask, 1 = included
 * @param {number} width mask width in pixels
 * @param {number} height mask height in pixels
 * @param {{tolerance?: number, maxContours?: number}} [opts]
 *   `tolerance`: Douglas-Peucker simplification tolerance, in working-image
 *   px (brief 3.1: ~1px). `maxContours`: cap on the number of returned
 *   contours (brief "watch for": someone scribbling can produce many small
 *   blobs) — kept largest-AREA-first (shoelace formula; area rather than
 *   perimeter because a large-perimeter sliver is visually less significant
 *   than a large-area blob, and area is what the customer is actually
 *   picking colour from).
 * @returns {Array<Array<{x:number,y:number}>>} closed polygons, each an
 *   ordered list of points (not repeating the first point at the end)
 */
function traceMaskContours(mask, width, height, { tolerance = 1, maxContours = 50 } = {}) {
  if (!mask || width <= 0 || height <= 0) return [];

  const sample = (x, y) => {
    if (x < 0 || x >= width || y < 0 || y >= height) return 0;
    return mask[y * width + x] ? 1 : 0;
  };

  // Segment endpoints always land on a half-integer coordinate, so `x*2`/
  // `y*2` are always integers — a cheap, exact map key (no float rounding
  // risk from e.g. `${x},${y}` on a computed float).
  const keyOf = (x, y) => `${Math.round(x * 2)}_${Math.round(y * 2)}`;
  const pointFromKey = (key) => {
    const [hx, hy] = key.split('_').map(Number);
    return { x: hx / 2, y: hy / 2 };
  };

  const nextPoint = new Map(); // segment-start key -> segment-end key

  for (let cy = -1; cy < height; cy++) {
    for (let cx = -1; cx < width; cx++) {
      const tl = sample(cx, cy);
      const tr = sample(cx + 1, cy);
      const br = sample(cx + 1, cy + 1);
      const bl = sample(cx, cy + 1);

      if (tl === tr && tr === br && br === bl) continue; // uniform cell, no crossings

      const corners = [tl, tr, br, bl]; // clockwise: TL, TR, BR, BL
      const edgePoints = [
        { x: cx + 0.5, y: cy }, // N: between TL and TR
        { x: cx + 1, y: cy + 0.5 }, // E: between TR and BR
        { x: cx + 0.5, y: cy + 1 }, // S: between BR and BL
        { x: cx, y: cy + 0.5 }, // W: between BL and TL
      ];

      const transitions = [];
      for (let i = 0; i < 4; i++) {
        const a = corners[i];
        const b = corners[(i + 1) % 4];
        if (a !== b) {
          transitions.push({ enter: a === 0, point: edgePoints[i] });
        }
      }
      if (transitions.length === 0) continue;

      const firstEnter = transitions.findIndex((t) => t.enter);
      const ordered = transitions.slice(firstEnter).concat(transitions.slice(0, firstEnter));

      for (let i = 0; i + 1 < ordered.length; i += 2) {
        const start = ordered[i];
        const end = ordered[i + 1];
        nextPoint.set(keyOf(start.point.x, start.point.y), keyOf(end.point.x, end.point.y));
      }
    }
  }

  const visited = new Set();
  const rawPolygons = [];
  for (const startKey of nextPoint.keys()) {
    if (visited.has(startKey)) continue;
    const poly = [];
    let curKey = startKey;
    const maxSteps = nextPoint.size + 1;
    let steps = 0;
    while (!visited.has(curKey) && steps++ < maxSteps) {
      visited.add(curKey);
      poly.push(pointFromKey(curKey));
      const nk = nextPoint.get(curKey);
      if (nk === undefined) break; // shouldn't happen; defensive
      curKey = nk;
    }
    if (poly.length >= 3) rawPolygons.push(poly);
  }

  const simplified = rawPolygons.map((poly) => simplifyClosedPolygon(poly, tolerance));

  simplified.sort((a, b) => polygonArea(b) - polygonArea(a));
  return simplified.slice(0, maxContours);
}

/**
 * Shoelace-formula polygon area (unsigned). Used to rank contours by size
 * for the `maxContours` cap.
 */
function polygonArea(points) {
  let area = 0;
  for (let i = 0; i < points.length; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % points.length];
    area += p1.x * p2.y - p2.x * p1.y;
  }
  return Math.abs(area) / 2;
}

function perpendicularDistance(pt, lineStart, lineEnd) {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  if (dx === 0 && dy === 0) {
    return Math.hypot(pt.x - lineStart.x, pt.y - lineStart.y);
  }
  const t = ((pt.x - lineStart.x) * dx + (pt.y - lineStart.y) * dy) / (dx * dx + dy * dy);
  const projX = lineStart.x + t * dx;
  const projY = lineStart.y + t * dy;
  return Math.hypot(pt.x - projX, pt.y - projY);
}

/**
 * Standard recursive Douglas-Peucker simplification of an open polyline.
 * @param {{x:number,y:number}[]} points
 * @param {number} tolerance
 * @returns {{x:number,y:number}[]}
 */
function douglasPeucker(points, tolerance) {
  if (points.length < 3) return points.slice();

  let maxDist = 0;
  let index = 0;
  const end = points.length - 1;
  for (let i = 1; i < end; i++) {
    const d = perpendicularDistance(points[i], points[0], points[end]);
    if (d > maxDist) {
      maxDist = d;
      index = i;
    }
  }

  if (maxDist > tolerance) {
    const left = douglasPeucker(points.slice(0, index + 1), tolerance);
    const right = douglasPeucker(points.slice(index), tolerance);
    return left.slice(0, -1).concat(right);
  }

  return [points[0], points[end]];
}

/**
 * Douglas-Peucker for a CLOSED polygon. Splitting the loop at two real,
 * well-separated anchor points (`points[0]` and the point roughly opposite
 * it, `points[mid]`) and running standard open-polyline DP independently on
 * each resulting arc — rather than the simpler "duplicate the first point
 * as a zero-length anchor line" approach — matters here: a zero-length
 * anchor degrades `perpendicularDistance` to plain distance-from-a-single-
 * point, which for a roughly round/square contour tends to keep only the
 * single farthest point and erase real corners, visibly shrinking the
 * simplified shape. Splitting into two arcs, each with a genuine chord
 * endpoint-to-endpoint line, keeps the simplification anchored to the
 * actual shape.
 *
 * Very small selections (brief "watch for"): a tiny contour (e.g. a
 * single-pixel dab traces to a 4-point diamond) can simplify away to fewer
 * than 3 points at this tolerance. Rather than let it collapse to nothing,
 * fall back to the unsimplified polygon so it still renders visibly.
 */
function simplifyClosedPolygon(points, tolerance) {
  if (points.length < 4) return points.slice();

  const mid = Math.floor(points.length / 2);
  const arc1 = points.slice(0, mid + 1); // points[0] .. points[mid]
  const arc2 = points.slice(mid).concat([points[0]]); // points[mid] .. points[n-1], points[0]

  const simplifiedArc1 = douglasPeucker(arc1, tolerance);
  const simplifiedArc2 = douglasPeucker(arc2, tolerance);

  // Each arc's last point is the other arc's first point (the shared
  // anchors) — drop one copy of each when stitching back into a single
  // closed loop (not repeating the first point at the end).
  const combined = simplifiedArc1.slice(0, -1).concat(simplifiedArc2.slice(0, -1));

  return combined.length >= 3 ? combined : points;
}

function hexToRgb(hex) {
  if (!hex) return null;
  const clean = hex.replace('#', '').trim();
  if (clean.length !== 6) return null;
  const num = parseInt(clean, 16);
  if (Number.isNaN(num)) return null;
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255,
  };
}

function hexToRgbTuple(hex) {
  const rgb = hexToRgb(hex);
  return rgb ? [rgb.r, rgb.g, rgb.b] : null;
}

function labToCss(lab, paletteMath) {
  const { labToRgb } = paletteMath;
  const rgb = labToRgb(lab);
  return `rgb(${Math.round(rgb.r)}, ${Math.round(rgb.g)}, ${Math.round(rgb.b)})`;
}

/**
 * Picks the variant matching `preferredTitle` (e.g. "100ml"), falling back
 * to the first available variant, per brief section 5.
 */
function pickVariant(product, preferredTitle) {
  if (!product || !product.variants || product.variants.length === 0) return null;

  if (preferredTitle) {
    const match = product.variants.find(
      (v) => v.available && v.title.toLowerCase().includes(preferredTitle.toLowerCase())
    );
    if (match) return match;
  }

  return product.variants.find((v) => v.available) || null;
}

/**
 * Minimal money formatter using the shop's money_format (Liquid setting),
 * falling back to a plain "$X.XX" if the format string is unrecognised.
 * Avoids pulling in Intl currency data we don't otherwise need.
 */
function formatMoney(cents, moneyFormat) {
  // Shopify's Liquid money type (`{{ v.price }}`) serializes as an integer
  // number of cents when interpolated directly, not a decimal dollar amount.
  const amount = (Number(cents) || 0) / 100;
  const formatted = amount.toFixed(2);
  const plainFormat = (moneyFormat || '').replace(/<[^>]*>/g, '').trim();

  if (plainFormat && plainFormat.includes('{{amount}}')) {
    return plainFormat.replace('{{amount}}', formatted);
  }
  return `$${formatted}`;
}

// Guarded so this module can be imported under Node (e.g. from a plain
// test script exercising the pure functions below) without needing a DOM.
if (typeof customElements !== 'undefined') {
  customElements.define('palette-photo-builder', PalettePhotoBuilder);
}

// Exported for standalone testing only (see palette-photo-builder.contours.test.mjs)
// — the class itself and the rest of the module are unaffected by this;
// nothing inside the file imports these back in.
export {
  traceMaskContours,
  douglasPeucker,
  simplifyClosedPolygon,
  polygonArea,
  mapDisplayToWorkingCoords,
  mapWorkingToDisplayCoords,
  workingToDisplayScale,
  sliderToFraction,
  fractionToSlider,
};
